import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Modals, { showUpdateConfirmation, showSubmitConfirmation } from "./Modals.tsx";
import Sidebar from "./Sidebar";
import axios from "axios";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import {
  FaUsers,
  FaUser,
  FaWifi,
  FaRedo,
  FaClock,
  FaUserTie,
  FaBook,
  FaSchool,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaCreditCard,
  FaPercent,
  FaCalculator,
  FaSearch,
  FaEye,
  FaPaperPlane,
  FaFileInvoiceDollar,
  FaHistory,
  FaSync,
  FaArrowLeft,
  FaArrowRight,
  FaInfoCircle,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimes,
  FaPhone,
  FaMapMarkerAlt,
  FaVenusMars,
  FaIdCard,
  FaChartLine,
  FaFilter,
  FaEdit,
  FaDatabase,
  FaBug,
  FaSpinner,
  FaExclamationCircle,
  FaUserGraduate,
  FaUserFriends,
  FaFamily,
  FaTimesCircle,
  FaExclamation,
  FaBell,
  FaCheck,
  FaArrowUp,
} from "react-icons/fa";
import {
  FiSearch,
  FiUser,
  FiUsers,
  FiHome,
  FiCreditCard,
  FiPhone,
  FiMail,
  FiCalendar,
  FiBook,
  FiPlus,
  FiSave,
  FiCheck,
  FiArrowRight,
  FiChevronRight,
  FiChevronLeft,
  FiRefreshCw,
  FiX,
  FiEdit,
  FiAlertCircle,
} from "react-icons/fi";
import "./ApplySoloStdFee.css";

// ========== NEW: Import Modals and Confirmation Dialogs ==========

// School Configuration
const SCHOOL_CONFIG = {
  admissionOpeningMonth: 2, // March (0 = January, 1 = February, 2 = March)
  academicYearStartMonth: 2, // March
  academicYearEndMonth: 1, // February (of next year)
  admissionYearStartMonth: 2, // March for admission year calculation
  currentYear: new Date().getFullYear(),
};


// Helper function to get month name
const getMonthName = (monthIndex) => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return months[monthIndex];
};

// Function to get admission start month
const getAdmissionStartMonth = (
  admissionOpeningMonth = SCHOOL_CONFIG.admissionOpeningMonth
) => {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  if (currentMonth >= admissionOpeningMonth) {
    return new Date(currentYear, admissionOpeningMonth, 1);
  } else {
    return new Date(currentYear - 1, admissionOpeningMonth, 1);
  }
};

// for family code
const API_BASE_URL_For_Fam_Code = "https://mhsdev.online/MHS-WEB/api";

// API base URL - Updated for production environment
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://mhsdev.online/MHS-WEB/api/manage_family_accounts.php";

// API Configuration
const API_CONFIG = {
  timeout: 30000,
  retries: 2,
  retryDelay: 1000,
};

// API Action Constants
const API_ACTIONS = {
  SEARCH_FAMILY_FEE: "search-family-fee",
  CHECK_TOTAL_FEE_EXP: "check-totalstdfeeandexp",
  UPDATE_TOTAL_FEE_EXP: "update-familyfeerecord",
  SUBMIT_FAMILY_FEE: "submit-family-fee",
  DEBUG_FEE_TABLES: "debug-fee-tables",
  VALIDATE_FEE_SETUP: "validate-fee-setup",
  TEST_STUDENT_STATUS: "test-student-fee-status",
  GET_VALIDATION_REPORT: "get-fee-validation-report",
  FIX_MISSING_SETUP: "fix-missing-fee-setup",
  CHECK_FAMILY_EXISTENCE: "check-family-existence",
  CHECK_FAMILY_FEE_RECORDS: "check-family-fee-records",
  AUTOCOMPLETE_FAMILY_CODE: "autocomplete-family-code",
};

// Helper function for API calls with enhanced error handling
const callAPI = async (
  action,
  data = null,
  method = "POST",
  retries = API_CONFIG.retries
) => {
  let lastError = null;
  let lastResponse = null;

  console.log(`=== callAPI STARTED ===`);
  console.log(`Action: ${action}`);
  console.log(`Method: ${method}`);
  console.log(`Data:`, data);
  console.log(`Retries configured: ${retries}`);

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      console.log(`\n--- Attempt ${attempt + 1} of ${retries + 1} ---`);

      const url = new URL(API_BASE_URL);
      url.searchParams.append("action", action);

      const config = {
        method: method.toUpperCase(),
        url: url.toString(),
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        timeout: API_CONFIG.timeout,
        validateStatus: function (status) {
          console.log(`Server returned status: ${status}`);
          return true;
        },
      };

      if (method.toUpperCase() === "GET" && data) {
        Object.keys(data).forEach((key) => {
          if (
            data[key] !== null &&
            data[key] !== undefined &&
            data[key] !== ""
          ) {
            url.searchParams.append(key, data[key]);
          }
        });
        config.url = url.toString();
        console.log(`GET URL with params: ${config.url}`);
      } else if (
        data &&
        (method.toUpperCase() === "POST" || method.toUpperCase() === "PUT")
      ) {
        config.data = data;
        console.log(`Request data:`, data);
      }

      console.log(`📡 Making API call to: ${config.url}`);

      const response = await axios(config);
      lastResponse = response.data;

      console.log(`✅ API Response received for ${action}:`);
      console.log(`   Status: ${response.status} ${response.statusText}`);

      if (response.status >= 400) {
        console.log(`⚠️ HTTP Error ${response.status} detected`);

        let serverErrorMessage = "Unknown server error";

        if (response.data) {
          if (typeof response.data === "string") {
            serverErrorMessage = response.data;
          } else if (response.data.error) {
            serverErrorMessage = response.data.error;
          } else if (response.data.message) {
            serverErrorMessage = response.data.message;
          } else {
            serverErrorMessage = JSON.stringify(response.data);
          }
        }

        console.log(`Server error message: "${serverErrorMessage}"`);

        throw {
          message: serverErrorMessage,
          response: response.data,
          status: response.status,
          statusText: response.statusText,
          isHttpError: true,
          isApiError: true,
          config: {
            url: config.url,
            method: config.method,
            data: config.data,
          },
        };
      }

      if (response.data && response.data.error) {
        console.log(`⚠️ API Error in response body: "${response.data.error}"`);

        throw {
          message: response.data.error,
          response: response.data,
          status: response.status,
          isHttpError: false,
          isApiError: true,
          isResponseError: true,
          config: {
            url: config.url,
            method: config.method,
            data: config.data,
          },
        };
      }

      if (!response.data) {
        console.log(`⚠️ Empty response received`);
        throw {
          message: "Empty response from server",
          status: response.status,
          isHttpError: false,
          isApiError: true,
          isEmptyResponse: true,
        };
      }

      console.log(`🎉 API call ${action} successful on attempt ${attempt + 1}`);
      return response.data;
    } catch (error) {
      lastError = error;

      console.error(`❌ API Error ${action} (Attempt ${attempt + 1}):`);
      console.error(`   Error message: ${error.message}`);

      if (attempt < retries) {
        const delay = API_CONFIG.retryDelay * (attempt + 1);
        console.warn(
          `⏳ Retrying in ${delay}ms... (${retries - attempt} retries left)`
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      console.log(`🚫 All retries exhausted for ${action}`);

      if (error.response) {
        const serverError =
          error.response.data?.error ||
          error.response.data?.message ||
          error.message ||
          `Server Error: ${error.response.status} ${error.response.statusText}`;

        return {
          error: serverError,
          message: serverError,
          _httpStatus: error.response.status,
          _statusText: error.response.statusText,
          _isHttpError: true,
          _isApiError: true,
          _isServerError: true,
          _retriesAttempted: attempt,
          _lastUrl: error.config?.url,
          ...(error.response.data || {}),
        };
      } else if (error.request) {
        return {
          error: "No response from server",
          message:
            "No response from server. Please check your network connection and try again.",
          status: 0,
          _isNetworkError: true,
          _isHttpError: false,
          _isApiError: true,
          _retriesAttempted: attempt,
          _lastUrl: error.config?.url,
        };
      } else if (error.isApiError) {
        return {
          error: error.message,
          message: error.message,
          _isHttpError: false,
          _isApiError: true,
          _isResponseError: true,
          _retriesAttempted: attempt,
          _lastUrl: error.config?.url,
          ...(error.response || {}),
        };
      } else if (error.code === "ECONNABORTED") {
        return {
          error: "Request timeout",
          message: "Server took too long to respond. Please try again.",
          _isTimeoutError: true,
          _isNetworkError: true,
          _isHttpError: false,
          _isApiError: true,
          _retriesAttempted: attempt,
          _lastUrl: error.config?.url,
        };
      } else if (error.isNetworkError) {
        return {
          error: "Network error",
          message:
            "Network error occurred. Please check your internet connection.",
          _isNetworkError: true,
          _isHttpError: false,
          _isApiError: true,
          _retriesAttempted: attempt,
          _lastUrl: error.config?.url,
        };
      } else {
        return {
          error: error.message || "Unknown error occurred",
          message: error.message || "An unknown error occurred",
          _isUnknownError: true,
          _isHttpError: false,
          _isApiError: true,
          _retriesAttempted: attempt,
          _lastUrl: error.config?.url,
        };
      }
    }
  }

  console.error(`Unexpected exit from callAPI for ${action}`);
  return {
    error: "Unexpected error in API call",
    message: "An unexpected error occurred while making the API call",
    _isUnexpectedError: true,
    _retriesAttempted: retries,
  };
};

// Enhanced API wrapper with specific methods
const api = {
  searchFamilyFee: (familyCode) =>
    callAPI(API_ACTIONS.SEARCH_FAMILY_FEE, { familyCode }, "POST"),

  checkTotalFeeExp: (familyCode) =>
    callAPI(
      API_ACTIONS.CHECK_TOTAL_FEE_EXP,
      { family_code: familyCode },
      "POST"
    ),

  updateTotalFeeExp: (data) =>
    callAPI(API_ACTIONS.UPDATE_TOTAL_FEE_EXP, data, "POST"),

  submitFamilyFee: (data) =>
    callAPI(API_ACTIONS.SUBMIT_FAMILY_FEE, data, "POST"),

  debugFeeTables: () => callAPI(API_ACTIONS.DEBUG_FEE_TABLES, {}, "GET"),

  validateFeeSetup: (studentId) =>
    callAPI(API_ACTIONS.VALIDATE_FEE_SETUP, { studentId }, "POST"),

  testStudentStatus: (studentId) =>
    callAPI(API_ACTIONS.TEST_STUDENT_STATUS, { student_id: studentId }, "POST"),

  getValidationReport: (studentId) =>
    callAPI(
      API_ACTIONS.GET_VALIDATION_REPORT,
      { student_id: studentId },
      "POST"
    ),

  fixMissingSetup: (studentId, autoCreate = false) =>
    callAPI(
      API_ACTIONS.FIX_MISSING_SETUP,
      { student_id: studentId, auto_create: autoCreate },
      "POST"
    ),

  checkFamilyExistence: (familyCode) =>
    callAPI(
      API_ACTIONS.CHECK_FAMILY_EXISTENCE,
      { family_code: familyCode },
      "POST"
    ),

  checkFamilyFeeRecords: (familyCode) =>
    callAPI(
      API_ACTIONS.CHECK_FAMILY_FEE_RECORDS,
      { family_code: familyCode },
      "POST"
    ),

  autocompleteFamilyCode: (query) =>
    callAPI(API_ACTIONS.AUTOCOMPLETE_FAMILY_CODE, { query }, "GET"),
};

const ApplyFamStdFee = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const className = location.state?.className || "Play Group";
  const inputRef = useRef(null);

  // ========== MODAL STATES ==========
  const [isFamilyCheckOpen, setIsFamilyCheckOpen] = useState(false);
  const [isFamilyDetailsOpen, setIsFamilyDetailsOpen] = useState(false);
  const [isStudentDetailsOpen, setIsStudentDetailsOpen] = useState(false);
  const [isFeeDetailsOpen, setIsFeeDetailsOpen] = useState(false);
  const [isFeeRecordsOpen, setIsFeeRecordsOpen] = useState(false);
  const [isUpdateSuccessOpen, setIsUpdateSuccessOpen] = useState(false);
  const [selectedStudentItem, setSelectedStudentItem] = useState(null);
  const [feeRecordsData, setFeeRecordsData] = useState(null);
  const [familyCheckResult, setFamilyCheckResult] = useState(null);


  // State for Family Code
  const [familyCode, setFamilyCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [familyCodeSuggestions, setFamilyCodeSuggestions] = useState([]);

  // Alert System State
  const [alerts, setAlerts] = useState([]);
  const [alertHistory, setAlertHistory] = useState([]);
  const [showAlertHistory, setShowAlertHistory] = useState(false);
  const [maxAlerts] = useState(5);

  // Family Data State
  const [familyData, setFamilyData] = useState(null);
  const [students, setStudents] = useState([]);
  const [totalStudents, setTotalStudents] = useState(0);

  // Get current date for admission logic
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  // Calculate proper start month based on admission logic
  const calculateStartMonth = () => {
    const { admissionOpeningMonth } = SCHOOL_CONFIG;

    if (currentMonth >= admissionOpeningMonth) {
      return new Date(currentYear, admissionOpeningMonth, 1);
    } else {
      return new Date(currentYear, currentMonth, 1);
    }
  };

  // Calculate end month based on start month
  const calculateEndMonth = (startDate) => {
    const { academicYearStartMonth, academicYearEndMonth } = SCHOOL_CONFIG;
    const start = new Date(startDate);

    const academicYear = start.getFullYear();
    const endYear =
      start.getMonth() < academicYearStartMonth
        ? academicYear + 1
        : academicYear + 2;

    return new Date(endYear, academicYearEndMonth, 1);
  };

  // Months Setting - UPDATED WITH ADMISSION LOGIC
  const [startMonth, setStartMonth] = useState(calculateStartMonth());
  const [endMonth, setEndMonth] = useState(
    calculateEndMonth(calculateStartMonth())
  );

  // Admission Info State
  const [admissionInfo, setAdmissionInfo] = useState({
    openingMonth: SCHOOL_CONFIG.admissionOpeningMonth,
    academicYear: `${currentYear}-${currentYear + 1}`,
  });

  // Fee Calculations
  const [totalFee, setTotalFee] = useState(0);
  const [totalPaid, setTotalPaid] = useState("");
  const [lastPending, setLastPending] = useState(0);
  const [finalTotal, setFinalTotal] = useState(0);
  const [monthCount, setMonthCount] = useState(0);
  const [discountPercentage, setDiscountPercentage] = useState(0);

  // Existing Records and Update Mode
  const [existingTotalRecord, setExistingTotalRecord] = useState(null);
  const [isTotalRecordUpdateMode, setIsTotalRecordUpdateMode] = useState(false);
  const [familyExistenceCheck, setFamilyExistenceCheck] = useState(null);
  const [familyFeeExists, setFamilyFeeExists] = useState(false);

  // Fee Summary - Made editable for poor students
  const [feeSummary, setFeeSummary] = useState({
    totalAnnualCharges: 0,
    totalAdmissionFee: 0,
    totalWorkbookCharges: 0,
    totalBookCharges: 0,
    totalMonthlyFee: 0,
    totalPayable: 0,
  });

  // System State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [apiLogs, setApiLogs] = useState([]);
  const [systemStatus, setSystemStatus] = useState("ready");
  const [apiEndpoint, setApiEndpoint] = useState(API_BASE_URL);

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Load family code from URL params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const storedCode = searchParams.get("familyCode");
    if (storedCode) {
      setFamilyCode(storedCode);
    }
  }, [location]);

  // Update end month when start month changes
  useEffect(() => {
    setEndMonth(calculateEndMonth(startMonth));
  }, [startMonth]);

  // ===================== ALERT SYSTEM FUNCTIONS =====================
  const addAlert = useCallback(
    (message, type = "info", duration = 5000) => {
      const id = Date.now();
      const newAlert = {
        id,
        message,
        type,
        duration,
        timestamp: new Date(),
        progress: 100,
        exiting: false,
      };

      setAlerts((prev) => {
        const updatedAlerts = [newAlert, ...prev.slice(0, maxAlerts - 1)];
        return updatedAlerts;
      });

      setAlertHistory((prev) => {
        const updatedHistory = [newAlert, ...prev.slice(0, 49)];
        return updatedHistory;
      });

      if (type !== "loading") {
        const progressInterval = setInterval(() => {
          setAlerts((prev) =>
            prev.map((alert) =>
              alert.id === id
                ? {
                    ...alert,
                    progress: Math.max(
                      0,
                      alert.progress - (100 / duration) * 100
                    ),
                  }
                : alert
            )
          );
        }, 100);

        const exitTimeout = setTimeout(() => {
          setAlerts((prev) =>
            prev.map((alert) =>
              alert.id === id ? { ...alert, exiting: true } : alert
            )
          );

          setTimeout(() => {
            setAlerts((prev) => prev.filter((alert) => alert.id !== id));
            clearInterval(progressInterval);
          }, 300);
        }, duration);

        return id;
      }

      return id;
    },
    [maxAlerts]
  );

  const removeAlert = useCallback((id) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  }, []);

  const clearAllAlerts = useCallback(() => {
    setAlerts((prev) => prev.map((alert) => ({ ...alert, exiting: true })));
    setTimeout(() => {
      setAlerts([]);
    }, 300);
  }, []);

  const addApiErrorAlert = useCallback(
    (error, context = "") => {
      const errorMessage =
        error.message || error.error || "An unknown error occurred";
      addAlert(
        `${context ? context + ": " : ""}${errorMessage}`,
        "error",
        8000
      );
    },
    [addAlert]
  );

  const addSuccessAlert = useCallback(
    (message) => {
      addAlert(message, "success", 4000);
    },
    [addAlert]
  );

  const addWarningAlert = useCallback(
    (message) => {
      addAlert(message, "warning", 6000);
    },
    [addAlert]
  );

  const addInfoAlert = useCallback(
    (message) => {
      addAlert(message, "info", 4000);
    },
    [addAlert]
  );

  const addLoadingAlert = useCallback(
    (message = "Processing...") => {
      return addAlert(message, "loading", 0);
    },
    [addAlert]
  );

  const removeLoadingAlert = useCallback(
    (loadingAlertId) => {
      if (loadingAlertId) {
        removeAlert(loadingAlertId);
      }
    },
    [removeAlert]
  );

  const clearAlertHistory = useCallback(() => {
    setAlertHistory([]);
    addSuccessAlert("Alert history cleared");
  }, [addSuccessAlert]);

  // ===================== END ALERT SYSTEM =====================

  // Format currency - UPDATED TO PKR Rs.
  const formatCurrency = useCallback((amount) => {
    return `PKR Rs. ${parseFloat(amount || 0).toLocaleString("en-PK", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }, []);

  // Handle one-time charges change
  const handleOneTimeChargeChange = useCallback((field, value) => {
    const numValue = parseFloat(value) || 0;

    setFeeSummary((prev) => {
      const updated = {
        ...prev,
        [field]: numValue,
      };

      const oneTimeTotal =
        (updated.totalAnnualCharges || 0) +
        (updated.totalAdmissionFee || 0) +
        (updated.totalWorkbookCharges || 0) +
        (updated.totalBookCharges || 0);

      const totalPayable = oneTimeTotal + (prev.totalMonthlyFee || 0);

      return {
        ...updated,
        totalPayable: totalPayable,
      };
    });
  }, []);

  // Handle monthly fee change
  const handleMonthlyFeeChange = useCallback((value) => {
    const numValue = parseFloat(value) || 0;

    setFeeSummary((prev) => {
      const updated = {
        ...prev,
        totalMonthlyFee: numValue,
      };

      const oneTimeTotal =
        (prev.totalAnnualCharges || 0) +
        (prev.totalAdmissionFee || 0) +
        (prev.totalWorkbookCharges || 0) +
        (prev.totalBookCharges || 0);

      const totalPayable = oneTimeTotal + numValue;

      return {
        ...updated,
        totalPayable: totalPayable,
      };
    });
  }, []);

  // Calculate total fee based on months
  const calculateTotalFee = useCallback(() => {
    if (!startMonth || !endMonth) return;

    const start = new Date(startMonth);
    const end = new Date(endMonth);

    let monthCount =
      (end.getFullYear() - start.getFullYear()) * 12 +
      (end.getMonth() - start.getMonth()) +
      1;

    if (monthCount < 0) monthCount = 0;

    setMonthCount(monthCount);

    const totalMonthlyFee = feeSummary.totalMonthlyFee || 0;
    const totalFeeAmount = totalMonthlyFee * monthCount;

    setTotalFee(totalFeeAmount);
  }, [startMonth, endMonth, feeSummary.totalMonthlyFee]);

  // Calculate final total
  const calculateFinalTotal = useCallback(() => {
    const totalPayable = feeSummary.totalPayable || 0;
    const discountAmount = (totalPayable * discountPercentage) / 100;
    const finalTotalAmount = totalPayable - discountAmount;

    setFinalTotal(finalTotalAmount);
    setLastPending(finalTotalAmount - parseFloat(totalPaid || 0));
  }, [feeSummary.totalPayable, discountPercentage, totalPaid]);

  // Handle total paid change
  const handleTotalPaidChange = useCallback(
    (value) => {
      const paidAmount = parseFloat(value) || 0;
      setTotalPaid(value);

      const totalPayable = feeSummary.totalPayable || 0;
      const discountAmount = (totalPayable * discountPercentage) / 100;
      const finalTotalAmount = totalPayable - discountAmount;

      setLastPending(finalTotalAmount - paidAmount);
    },
    [feeSummary.totalPayable, discountPercentage]
  );

  // Handle discount change
  const handleDiscountChange = useCallback(
    (value) => {
      let discountValue = parseFloat(value);
      if (isNaN(discountValue) || discountValue < 0) discountValue = 0;
      if (discountValue > 100) discountValue = 100;

      setDiscountPercentage(discountValue);

      const totalPayable = feeSummary.totalPayable || 0;
      const discountAmount = (totalPayable * discountValue) / 100;
      const finalTotalAmount = totalPayable - discountAmount;

      setFinalTotal(finalTotalAmount);
      setLastPending(finalTotalAmount - parseFloat(totalPaid || 0));
    },
    [feeSummary.totalPayable, totalPaid]
  );

  // Calculate month count when dates change
  useEffect(() => {
    calculateTotalFee();
  }, [startMonth, endMonth, calculateTotalFee]);

  // Calculate final total when discount or total payable changes
  useEffect(() => {
    calculateFinalTotal();
  }, [
    feeSummary.totalPayable,
    discountPercentage,
    totalPaid,
    calculateFinalTotal,
  ]);

  // Add API log
  const addApiLog = useCallback((log) => {
    setApiLogs((prev) => [
      {
        id: Date.now() + Math.random(),
        timestamp: new Date().toISOString(),
        ...log,
      },
      ...prev.slice(0, 50),
    ]);
  }, []);

  // Validate start month selection
  const validateStartMonth = useCallback(
    (selectedDate) => {
      const { admissionOpeningMonth } = SCHOOL_CONFIG;
      const selectedYear = selectedDate.getFullYear();
      const selectedMonth = selectedDate.getMonth();

      const admissionStart = getAdmissionStartMonth(admissionOpeningMonth);

      if (selectedDate < admissionStart) {
        addWarningAlert(
          `Warning: Selected start month (${getMonthName(selectedMonth)}) ` +
            `is before current admission period (${getMonthName(
              admissionOpeningMonth
            )}). ` +
            `Ensure this is correct for mid-year transfers.`
        );
      }

      return true;
    },
    [addWarningAlert]
  );

  // ===================== NEW FUNCTION: Check Family Existence =====================
  const checkFamilyExistence = async (familyCode) => {
    if (!familyCode || familyCode.trim() === "") {
      addWarningAlert("Please enter a Family Code to check");
      return null;
    }

    const loadingAlertId = addLoadingAlert("Checking family existence...");

    try {
      addApiLog({
        action: API_ACTIONS.CHECK_FAMILY_EXISTENCE,
        familyCode: familyCode,
        status: "started",
      });

      const response = await api.checkFamilyExistence(familyCode);

      addApiLog({
        action: API_ACTIONS.CHECK_FAMILY_EXISTENCE,
        familyCode: familyCode,
        status: response.error ? "error" : "success",
        data: response,
      });

      removeLoadingAlert(loadingAlertId);

      if (response.error) {
        addWarningAlert(`Family existence check failed: ${response.error}`);
        return null;
      }

      setFamilyExistenceCheck(response);
      setFamilyFeeExists(response.exists);

      if (!response.exists && response.action === "create_new") {
        addInfoAlert(response.message);
        return response;
      }

      if (response.exists && response.action === "update_existing") {
        // Show the FamilyCheckDialog via the Modals component
        setFamilyCheckResult(response);
        setIsFamilyCheckOpen(true);
        return response;
      }

      return response;
    } catch (error) {
      removeLoadingAlert(loadingAlertId);
      addApiErrorAlert(error, "Family existence check failed");
      return null;
    }
  };

  // ===================== NEW FUNCTION: Check Family Fee Records =====================
  const fetchFamilyFeeRecords = async (familyCode) => {
    const loadingAlertId = addLoadingAlert("Fetching family fee records...");

    try {
      addApiLog({
        action: API_ACTIONS.CHECK_FAMILY_FEE_RECORDS,
        familyCode: familyCode,
        status: "started",
      });

      const response = await api.checkFamilyFeeRecords(familyCode);

      addApiLog({
        action: API_ACTIONS.CHECK_FAMILY_FEE_RECORDS,
        familyCode: familyCode,
        status: response.error ? "error" : "success",
        data: response,
      });

      removeLoadingAlert(loadingAlertId);

      if (response.error) {
        addWarningAlert(
          `Failed to fetch family fee records: ${response.error}`
        );
        return null;
      }

      return response;
    } catch (error) {
      removeLoadingAlert(loadingAlertId);
      addApiErrorAlert(error, "Fetch family fee records failed");
      return null;
    }
  };

  // ===================== UPDATED FUNCTION: Handle Search for Family =====================
  const handleSearch = async () => {
    if (isLoading) {
      addWarningAlert("Please wait for the current search to complete.");
      return;
    }

    if (!familyCode || familyCode.trim() === "") {
      addWarningAlert("Please enter a Family Code to search");
      return;
    }

    setIsLoading(true);
    setSystemStatus("checking_family");
    setSearchError(null);
    clearAllAlerts();

    setFamilyData(null);
    setStudents([]);
    setTotalStudents(0);
    setFeeSummary({
      totalAnnualCharges: 0,
      totalAdmissionFee: 0,
      totalWorkbookCharges: 0,
      totalBookCharges: 0,
      totalMonthlyFee: 0,
      totalPayable: 0,
    });
    setTotalFee(0);
    setFinalTotal(0);
    setTotalPaid("");
    setLastPending(0);
    setMonthCount(0);
    setExistingTotalRecord(null);
    setIsTotalRecordUpdateMode(false);
    setFamilyExistenceCheck(null);
    setFamilyFeeExists(false);

    const loadingAlertId = addLoadingAlert(
      "Checking family existence and fee setup..."
    );

    try {
      const existenceResult = await checkFamilyExistence(familyCode);

      if (!existenceResult) {
        throw new Error("Family existence check failed");
      }

      if (
        !existenceResult.exists &&
        existenceResult.action === "create_new" &&
        existenceResult.data?.student_count_in_family === 0
      ) {
        setSearchError(
          `Family code "${familyCode}" not found in student records`
        );
        setSystemStatus("error");
        return;
      }

      if (!existenceResult.exists && existenceResult.action === "create_new") {
        await loadFamilyStudents(familyCode, false);
        return;
      }

      // Family exists with fee setup – handled in checkFamilyExistence (modal opens)
    } catch (error) {
      console.error("Search error:", error);
      setSearchError(error.message);

      removeLoadingAlert(loadingAlertId);

      addApiLog({
        action: "FAMILY_SEARCH",
        familyCode: familyCode,
        status: "error",
        error: error.message,
      });

      if (
        error.message.includes("Family not found") ||
        error.message.includes("not found")
      ) {
        Swal.fire({
          title: "Family Not Found",
          text: `Family code "${familyCode}" was not found in the system.`,
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#ef4444",
        });
      } else {
        addWarningAlert(error.message || "Search failed");
      }

      setSystemStatus("error");
    } finally {
      setIsLoading(false);
      removeLoadingAlert(loadingAlertId);
    }
  };

  // ===================== NEW FUNCTION: Load Family Students =====================
  const loadFamilyStudents = async (familyCode, isUpdateMode = false) => {
    setIsLoading(true);
    setSystemStatus("loading_family");

    const loadingAlertId = addLoadingAlert("Loading family students...");

    try {
      addApiLog({
        action: API_ACTIONS.SEARCH_FAMILY_FEE,
        familyCode: familyCode,
        status: "started",
      });

      const response = await api.searchFamilyFee(familyCode);

      addApiLog({
        action: API_ACTIONS.SEARCH_FAMILY_FEE,
        familyCode: familyCode,
        status: response.error ? "error" : "success",
        data: response,
      });

      removeLoadingAlert(loadingAlertId);

      const hasError =
        response.error ||
        response._isApiError ||
        response._isHttpError ||
        (response.status && response.status >= 400) ||
        (response._httpStatus && response._httpStatus >= 400) ||
        (response.response && response.response.error) ||
        !response.success;

      if (hasError) {
        let errorMessage = "";

        if (response.error) {
          errorMessage = response.error;
        } else if (response.response && response.response.error) {
          errorMessage = response.response.error;
        } else if (response.message) {
          errorMessage = response.message;
        } else if (response.status && response.status === 404) {
          errorMessage = "No students found for this family";
        } else {
          errorMessage = "Family not found or server error";
        }

        if (
          errorMessage.includes("No students found") ||
          errorMessage.includes("Family not found") ||
          errorMessage.includes("404") ||
          errorMessage.toLowerCase().includes("not found")
        ) {
          setSearchError(`Family not found: ${familyCode}`);
        } else {
          addWarningAlert(errorMessage);
          setSearchError(errorMessage);
        }

        setSystemStatus("error");
        return;
      }

      if (
        response.success ||
        (response.students && response.students.length > 0)
      ) {
        setFamilyData(response);
        setStudents(response.students || []);
        setTotalStudents(
          response.totalStudents ||
            (response.students ? response.students.length : 0)
        );

        if (response.students && response.students.length > 0) {
          let totalAnnualCharges = 0;
          let totalAdmissionFee = 0;
          let totalWorkbookCharges = 0;
          let totalBookCharges = 0;
          let totalMonthlyFee = 0;
          let totalPayable = 0;

          response.students.forEach((studentWithFees) => {
            const feeDetails = studentWithFees.feeDetails || {};

            totalAnnualCharges += parseFloat(feeDetails.annual_charges) || 0;
            totalAdmissionFee += parseFloat(feeDetails.admission_fee) || 0;
            totalWorkbookCharges +=
              parseFloat(feeDetails.workbook_charges) || 0;
            totalBookCharges += parseFloat(feeDetails.book_charges) || 0;
            totalMonthlyFee += parseFloat(feeDetails.monthly_fee) || 0;
            totalPayable += parseFloat(feeDetails.total_payable) || 0;
          });

          setFeeSummary({
            totalAnnualCharges,
            totalAdmissionFee,
            totalWorkbookCharges,
            totalBookCharges,
            totalMonthlyFee,
            totalPayable,
          });

          if (isUpdateMode) {
            await loadExistingFamilyFeeRecords(familyCode);
          }

          calculateTotalFee();
          calculateFinalTotal();

          Swal.fire({
            title: "Success!",
            text: `Found ${response.students.length} student(s) in family ${familyCode}`,
            icon: "success",
            confirmButtonText: "OK",
            confirmButtonColor: "#4f46e5",
            timer: 3000,
            timerProgressBar: true,
          });

          setSystemStatus("family_loaded");
          setSearchError(null);
        } else {
          addWarningAlert(
            `Family ${familyCode} exists but has no active students.`
          );
          setSystemStatus("error");
        }
      } else {
        throw new Error(
          response.error || "Family not found or invalid response format"
        );
      }
    } catch (error) {
      console.error("Load family students error:", error);
      setSearchError(error.message);

      removeLoadingAlert(loadingAlertId);

      addApiLog({
        action: API_ACTIONS.SEARCH_FAMILY_FEE,
        familyCode: familyCode,
        status: "error",
        error: error.message,
      });

      addWarningAlert(error.message || "Failed to load family students");
      setSystemStatus("error");
    } finally {
      setIsLoading(false);
    }
  };

  // ===================== NEW FUNCTION: Load Existing Family Fee Records =====================
  const loadExistingFamilyFeeRecords = async (familyCode) => {
    try {
      const feeRecordsResponse = await fetchFamilyFeeRecords(familyCode);

      if (
        feeRecordsResponse &&
        feeRecordsResponse.exists &&
        feeRecordsResponse.records
      ) {
        setExistingTotalRecord(feeRecordsResponse.records[0] || null);
        setIsTotalRecordUpdateMode(true);

        if (feeRecordsResponse.records[0]) {
          const existing = feeRecordsResponse.records[0];
          setDiscountPercentage(parseFloat(existing.discount) || 0);
          setTotalFee(parseFloat(existing.total_fee) || 0);
          setTotalPaid(parseFloat(existing.total_paid) || 0);
          setLastPending(parseFloat(existing.last_pendings) || 0);

          setFeeSummary((prev) => ({
            ...prev,
            totalAnnualCharges:
              parseFloat(existing.annual_charges) || prev.totalAnnualCharges,
            totalAdmissionFee:
              parseFloat(existing.admission_fee) || prev.totalAdmissionFee,
            totalWorkbookCharges:
              parseFloat(existing.workbook_charges) ||
              prev.totalWorkbookCharges,
            totalBookCharges:
              parseFloat(existing.book_charges) || prev.totalBookCharges,
            totalMonthlyFee:
              parseFloat(existing.monthly_fee) || prev.totalMonthlyFee,
            totalPayable:
              parseFloat(existing.total_payable) || prev.totalPayable,
          }));
        }

        Swal.fire({
          title: "Fee Record Exists",
          html: `Fee record for family <strong>${familyCode}</strong> already exists.<br>You can update the existing record.`,
          icon: "info",
          confirmButtonText: "OK",
          confirmButtonColor: "#3b82f6",
        });
      } else {
        setExistingTotalRecord(null);
        setIsTotalRecordUpdateMode(false);
      }
    } catch (error) {
      console.error("Error loading existing fee records:", error);
    }
  };

  // ===================== NEW FUNCTION: Handle Family Check Dialog =====================
  const handleFamilyCheckDialog = async (action) => {
    // This is called from the Modals component callbacks
    if (action === "update") {
      addInfoAlert("Loading existing family fee records for update...");
      await loadFamilyStudents(familyCode, true);
    } else if (action === "view") {
      const feeRecords = await fetchFamilyFeeRecords(familyCode);
      if (feeRecords) {
        setFeeRecordsData(feeRecords);
        setIsFeeRecordsOpen(true);
      }
    } else if (action === "submit_new") {
      addWarningAlert(
        "Creating new fee record. This may create duplicate entries."
      );
      await loadFamilyStudents(familyCode, false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFamilyData(null);
    setStudents([]);
    setTotalStudents(0);
    setFeeSummary({
      totalAnnualCharges: 0,
      totalAdmissionFee: 0,
      totalWorkbookCharges: 0,
      totalBookCharges: 0,
      totalMonthlyFee: 0,
      totalPayable: 0,
    });
    setTotalFee(0);
    setFinalTotal(0);
    setTotalPaid("");
    setLastPending(0);
    setMonthCount(0);
    setDiscountPercentage(0);
    setExistingTotalRecord(null);
    setIsTotalRecordUpdateMode(false);
    setFamilyExistenceCheck(null);
    setFamilyFeeExists(false);
    setSearchError(null);
    setSystemStatus("ready");
    setIsFamilyCheckOpen(false);
    setFamilyCheckResult(null);
    clearAllAlerts();

    setStartMonth(calculateStartMonth());
    setEndMonth(calculateEndMonth(calculateStartMonth()));
  };

  // Fetch autocomplete suggestions from PHP backend
  const fetchFamilyCodeSuggestions = async (query) => {
    if (query.length < 2) {
      setFamilyCodeSuggestions([]);
      return;
    }

    try {
      const response = await api.autocompleteFamilyCode(query);

      if (response && response.success && response.suggestions) {
        const suggestions = response.suggestions.map((code) => ({
          family_code: code,
          family_name: `Family ${code}`,
        }));
        setFamilyCodeSuggestions(suggestions);
      } else {
        const fallbackResponse = await axios.get(
          `${API_BASE_URL_For_Fam_Code}/manage_family_accounts.php?action=search-family`,
          { params: { type: "family-code", query: query } }
        );
        if (fallbackResponse.data.results) {
          setFamilyCodeSuggestions(fallbackResponse.data.results);
        }
      }
    } catch (error) {
      console.error("Error fetching family code suggestions:", error);
      try {
        const fallbackResponse = await axios.get(
          `${API_BASE_URL_For_Fam_Code}/students.php?action=search-family`,
          { params: { type: "family-code", query: query } }
        );
        if (fallbackResponse.data.results) {
          setFamilyCodeSuggestions(fallbackResponse.data.results);
        }
      } catch (fallbackError) {
        console.error("Fallback also failed:", fallbackError);
      }
    }
  };

  // Handle family code change with suggestions
  const handleFamilyCodeChange = (e) => {
    const value = e.target.value;
    setFamilyCode(value);
    fetchFamilyCodeSuggestions(value);
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion, type) => {
    if (type === "familyCode") {
      setFamilyCode(suggestion.family_code);
      setFamilyCodeSuggestions([]);
    }
  };

  // Calculate fees summary
  const calculateSummary = () => {
    return {
      oneTimeCharges:
        (feeSummary.totalAnnualCharges || 0) +
        (feeSummary.totalAdmissionFee || 0) +
        (feeSummary.totalWorkbookCharges || 0) +
        (feeSummary.totalBookCharges || 0),
      monthlyFee: feeSummary.totalMonthlyFee || 0,
      totalFee,
      totalPayable: feeSummary.totalPayable || 0,
      discount: discountPercentage || 0,
      discountAmount:
        ((feeSummary.totalPayable || 0) * discountPercentage) / 100,
      finalTotal,
      paid: parseFloat(totalPaid) || 0,
      balance: lastPending,
    };
  };

  const summary = calculateSummary();

  // Handle print receipt
  const handlePrintReceipt = () => {
    if (!familyCode || !totalPaid) {
      addWarningAlert(
        "Please complete a payment first before printing receipt."
      );
      return;
    }

    addInfoAlert("Generating receipt... Please check your print dialog.");

    const receiptNo = `FAM-${Date.now().toString().slice(-8)}`;
    const receiptDate = moment().format("DD-MM-YYYY hh:mm A");
    const paymentMonth = moment(startMonth).format("MMMM YYYY");
    const action = isTotalRecordUpdateMode ? "updated" : "created";

    const receiptWindow = window.open("", "_blank");
    receiptWindow.document.write(`
      <html>
        <head>
          <title>Family Fee Receipt - ${familyCode}</title>
          <style>
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              padding: 20px; 
              background: #f7fafc;
            }
            .receipt { 
              max-width: 500px; 
              margin: 0 auto; 
              border: 2px solid #2d3748; 
              padding: 30px; 
              background: white;
              border-radius: 10px;
              box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
            }
            .header { 
              text-align: center; 
              margin-bottom: 25px; 
              border-bottom: 2px solid #e2e8f0;
              padding-bottom: 20px;
            }
            .header h1 { 
              color: ${action === "updated" ? "#f39c12" : "#2d3748"};
              margin: 0 0 10px;
              font-size: 24px;
            }
            .header h2 { 
              color: #667eea;
              margin: 0;
              font-size: 20px;
            }
            .header .action-badge {
              display: inline-block;
              padding: 4px 12px;
              background: ${action === "updated" ? "#f39c12" : "#27ae60"};
              color: white;
              border-radius: 20px;
              font-size: 12px;
              margin-top: 8px;
            }
            .school-info {
              color: #718096;
              font-size: 14px;
              margin: 10px 0;
            }
            .details { 
              margin: 25px 0; 
            }
            .detail-row { 
              display: flex; 
              justify-content: space-between; 
              margin: 10px 0; 
              padding: 8px 0;
              border-bottom: 1px solid #e2e8f0;
            }
            .detail-row:last-child {
              border-bottom: none;
            }
            .total { 
              font-weight: bold; 
              border-top: 2px solid #2d3748; 
              padding-top: 15px; 
              margin-top: 20px; 
            }
            .footer { 
              text-align: center; 
              margin-top: 30px; 
              font-size: 12px; 
              color: #718096;
              border-top: 1px solid #e2e8f0;
              padding-top: 15px;
            }
            .amount {
              font-weight: bold;
              color: #2d3748;
            }
            .signature {
              margin-top: 40px;
              text-align: right;
            }
            .signature-line {
              border-top: 1px solid #2d3748;
              width: 200px;
              margin-left: auto;
              margin-top: 40px;
            }
            .signature-label {
              text-align: right;
              margin-top: 5px;
              font-size: 12px;
            }
            .family-info {
              background: #edf2f7;
              padding: 10px;
              border-radius: 6px;
              margin: 10px 0;
            }
          </style>
        </head>
        <body>
          <div class="receipt">
            <div class="header">
              <h1>FAMILY FEE RECEIPT</h1>
              <h2>Official Family Payment Record</h2>
              <div class="action-badge">${
                action === "updated"
                  ? "UPDATED FAMILY RECORD"
                  : "NEW FAMILY RECORD"
              }</div>
              <div class="school-info">Education Excellence Institute</div>
            </div>
            
            <div class="details">
              <div class="detail-row">
                <span>Receipt No:</span>
                <strong>${receiptNo}</strong>
              </div>
              <div class="detail-row">
                <span>Date & Time:</span>
                <span>${receiptDate}</span>
              </div>
              <div class="detail-row">
                <span>Family Code:</span>
                <strong>${familyCode}</strong>
              </div>
              <div class="family-info">
                <div><strong>Total Students:</strong> ${totalStudents}</div>
                <div><strong>Family Name:</strong> ${
                  students.length > 0 && students[0].student
                    ? students[0].student.father_name
                    : "N/A"
                }</div>
              </div>
              <div class="detail-row">
                <span>Payment Month:</span>
                <span>${paymentMonth}</span>
              </div>
              <div class="detail-row">
                <span>Period:</span>
                <span>${moment(startMonth).format("MMM YYYY")} - ${moment(
      endMonth
    ).format("MMM YYYY")}</span>
              </div>
              <div class="detail-row">
                <span>Months Count:</span>
                <span>${monthCount}</span>
              </div>
            </div>
            
            <div class="total">
              <div class="detail-row">
                <span>Total Monthly Fee:</span>
                <span class="amount">${formatCurrency(
                  feeSummary.totalMonthlyFee
                )}</span>
              </div>
              <div class="detail-row">
                <span>Total Payable:</span>
                <span class="amount">${formatCurrency(
                  feeSummary.totalPayable
                )}</span>
              </div>
              <div class="detail-row">
                <span>Discount (${discountPercentage}%):</span>
                <span class="amount">- ${formatCurrency(
                  summary.discountAmount
                )}</span>
              </div>
              <div class="detail-row">
                <span>Final Total:</span>
                <span class="amount">${formatCurrency(finalTotal)}</span>
              </div>
              <div class="detail-row">
                <span>Amount Paid:</span>
                <span class="amount" style="color: #48bb78;">${formatCurrency(
                  totalPaid
                )}</span>
              </div>
              <div class="detail-row">
                <span>Balance Due:</span>
                <span class="amount" style="color: ${
                  lastPending > 0 ? "#e53e3e" : "#48bb78"
                };">${formatCurrency(lastPending)}</span>
              </div>
            </div>
            
            <div class="signature">
              <div class="signature-line"></div>
              <div class="signature-label">Authorized Signature</div>
            </div>
            
            <div class="footer">
              <p>This is a computer generated receipt. No signature required.</p>
              <p>For any queries, contact: accounts@school.edu.pk</p>
              <p>Generated on: ${moment().format("DD-MM-YYYY hh:mm A")}</p>
            </div>
          </div>
        </body>
      </html>
    `);
    receiptWindow.document.close();
    receiptWindow.focus();
    receiptWindow.print();
  };

  // Clear form
  const handleClearForm = () => {
    Swal.fire({
      title: "Form Cleared",
      text: "All form data has been cleared.",
      icon: "info",
      confirmButtonText: "OK",
      timer: 2000,
    });
    resetForm();
    setFamilyCode("");
  };

  // Handle key navigation
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  // Test API functionality
  const testApiConnection = async () => {
    const loadingAlertId = addLoadingAlert();

    try {
      addApiLog({
        action: API_ACTIONS.DEBUG_FEE_TABLES,
        status: "started",
      });

      const response = await api.debugFeeTables();

      addApiLog({
        action: API_ACTIONS.DEBUG_FEE_TABLES,
        status: response.error ? "error" : "success",
        data: response,
      });

      removeLoadingAlert(loadingAlertId);

      if (response.error) {
        Swal.fire({
          title: "API Test Failed",
          text: response.error,
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#ef4444",
        });
      } else {
        Swal.fire({
          title: "API Test Successful",
          text: "Connection to the server is working properly.",
          icon: "success",
          confirmButtonText: "OK",
          confirmButtonColor: "#10b981",
          timer: 2000,
          timerProgressBar: true,
        });
      }
    } catch (error) {
      removeLoadingAlert(loadingAlertId);
      addWarningAlert(`API Connection Error: ${error.message}`);
    }
  };

  // Helper function for consistent Swal alerts
  const showSwal = (title, text, type = "info") => {
    const config = {
      title,
      text,
      showCloseButton: true,
      confirmButtonText: "OK",
      allowOutsideClick: true,
      allowEscapeKey: true,
      backdrop: true,
    };

    switch (type) {
      case "success":
        return Swal.fire({
          ...config,
          icon: "success",
          confirmButtonColor: "#10b981",
          timer: 3000,
          timerProgressBar: true,
        });
      case "error":
        return Swal.fire({
          ...config,
          icon: "error",
          confirmButtonColor: "#ef4444",
        });
      case "warning":
        return Swal.fire({
          ...config,
          icon: "warning",
          confirmButtonColor: "#f59e0b",
        });
      case "info":
        return Swal.fire({
          ...config,
          icon: "info",
          confirmButtonColor: "#3b82f6",
          timer: 2000,
        });
      default:
        return Swal.fire(config);
    }
  };

  // Show debug panel
  const showDebugPanel = () => {
    addInfoAlert("Debug Information");

    Swal.fire({
      title: "Debug Information",
      html: `
        <div style="text-align: left; max-height: 400px; overflow-y: auto;">
          <h4>System Status: ${systemStatus}</h4>
          <h4>Family Code: ${familyCode}</h4>
          <h4>Total Students: ${totalStudents}</h4>
          <h4>Family Fee Exists: ${familyFeeExists ? "Yes" : "No"}</h4>
          <h4>API Endpoint: ${apiEndpoint}</h4>
          <h4>Family Existence Check:</h4>
          <pre style="background: #f8f9fa; padding: 10px; border-radius: 5px; font-size: 12px;">
${JSON.stringify(familyExistenceCheck, null, 2)}
          </pre>
          <h4>Fee Summary:</h4>
          <pre style="background: #f8f9fa; padding: 10px; border-radius: 5px; font-size: 12px;">
${JSON.stringify(feeSummary, null, 2)}
          </pre>
          <h4>Existing Total Record:</h4>
          <pre style="background: #f8f9fa; padding: 10px; border-radius: 5px; font-size: 12px;">
${JSON.stringify(existingTotalRecord, null, 2)}
          </pre>
          <h4>Recent API Logs (${apiLogs.length}):</h4>
          <div style="background: #f8f9fa; padding: 10px; border-radius: 5px; font-size: 12px;">
            ${apiLogs
              .slice(0, 5)
              .map(
                (log, index) => `
              <div style="margin-bottom: 5px; padding: 5px; border-left: 3px solid ${
                log.status === "success"
                  ? "#27ae60"
                  : log.status === "error"
                  ? "#e74c3c"
                  : "#3498db"
              }; background: #fff;">
                <strong>${moment(log.timestamp).format(
                  "HH:mm:ss"
                )}</strong> - ${log.action}
                <span style="float: right; color: ${
                  log.status === "success"
                    ? "#27ae60"
                    : log.status === "error"
                    ? "#e74c3c"
                    : "#3498db"
                };">${log.status}</span>
              </div>
            `
              )
              .join("")}
          </div>
        </div>
      `,
      width: 800,
      confirmButtonText: "Close",
      showCancelButton: true,
      cancelButtonText: "Clear Logs",
      showCloseButton: true,
      allowOutsideClick: true,
      allowEscapeKey: true,
      backdrop: true,
    }).then((result) => {
      if (result.dismiss === Swal.DismissReason.cancel) {
        setApiLogs([]);
        addSuccessAlert("API logs have been cleared.");
      }
    });
  };

  // Clear API logs
  const clearApiLogs = () => {
    setApiLogs([]);
    addSuccessAlert("All API logs have been cleared.");
  };

  // Process update submission
  const processUpdateSubmission = async (familyRecordData) => {
    setIsSubmitting(true);
    setSystemStatus("updating");

    const loadingAlertId = addLoadingAlert();

    try {
      if (!existingTotalRecord?.id) {
        throw new Error("Missing record ID for update");
      }

      const updateData = {
        id: existingTotalRecord.id,
        ...familyRecordData,
      };

      addApiLog({
        action: API_ACTIONS.UPDATE_TOTAL_FEE_EXP,
        data: updateData,
        status: "started",
      });

      const updateResponse = await api.updateTotalFeeExp(updateData);

      addApiLog({
        action: API_ACTIONS.UPDATE_TOTAL_FEE_EXP,
        data: updateData,
        status: updateResponse.error ? "error" : "success",
        response: updateResponse,
      });

      removeLoadingAlert(loadingAlertId);

      if (updateResponse.error) {
        throw new Error(
          updateResponse.error ||
            updateResponse.message ||
            "Failed to update family record"
        );
      }

      if (updateResponse.success) {
        // Show the Update Success Modal instead of direct Swal
        setIsUpdateSuccessOpen(true);
        // Reset form after modal closes (handled in closeUpdateSuccess)
      } else {
        throw new Error(
          updateResponse.error || "Failed to update family record"
        );
      }
    } catch (error) {
      console.error("Update submission error:", error);
      addApiLog({
        action: API_ACTIONS.UPDATE_TOTAL_FEE_EXP,
        status: "error",
        error: error.message,
      });

      removeLoadingAlert(loadingAlertId);
      addWarningAlert(`Update Failed: ${error.message}`);
    } finally {
      setIsSubmitting(false);
      setSystemStatus("ready");
    }
  };

  // Process new submission
  const processNewSubmission = async (familyRecordData) => {
    setIsSubmitting(true);
    setSystemStatus("submitting");

    const loadingAlertId = addLoadingAlert();

    try {
      addApiLog({
        action: API_ACTIONS.SUBMIT_FAMILY_FEE,
        data: familyRecordData,
        status: "started",
      });

      const submitResponse = await api.submitFamilyFee(familyRecordData);

      addApiLog({
        action: API_ACTIONS.SUBMIT_FAMILY_FEE,
        data: familyRecordData,
        status: submitResponse.error ? "error" : "success",
        response: submitResponse,
      });

      removeLoadingAlert(loadingAlertId);

      if (submitResponse.error) {
        throw new Error(
          submitResponse.error ||
            submitResponse.message ||
            "Failed to submit family record"
        );
      }

      if (submitResponse.success) {
        addSuccessAlert(`New Family Fee Record Created Successfully!`);
        setTimeout(() => {
          resetForm();
          setFamilyCode("");
        }, 2000);
      } else {
        throw new Error(
          submitResponse.error || "Failed to submit family record"
        );
      }
    } catch (error) {
      console.error("Submission error:", error);
      addApiLog({
        action: API_ACTIONS.SUBMIT_FAMILY_FEE,
        status: "error",
        error: error.message,
      });

      removeLoadingAlert(loadingAlertId);
      addWarningAlert(`Submission Failed: ${error.message}`);
    } finally {
      setIsSubmitting(false);
      setSystemStatus("ready");
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!familyCode || !familyData) {
      Swal.fire({
        title: "Validation Error",
        text: "Please search for a family first before submitting.",
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#f59e0b",
      });
      return;
    }

    if (totalStudents === 0) {
      addWarningAlert(
        "No students found in this family. Cannot submit fee record."
      );
      return;
    }

    const paidAmount = parseFloat(totalPaid) || 0;
    const totalPayable = feeSummary.totalPayable || 0;

    if (paidAmount <= 0) {
      addWarningAlert("Please enter a valid payment amount greater than zero.");
      return;
    }

    if (paidAmount > totalPayable) {
      addWarningAlert("Total Paid cannot exceed Total Payable!");
      return;
    }

    const discountAmount = (totalPayable * discountPercentage) / 100;
    const finalTotalAmount = totalPayable - discountAmount;
    const lastPendings = finalTotalAmount - paidAmount;

    const familyName =
      students.length > 0 && students[0].student
        ? students[0].student.father_name
        : "";

    const familyRecordData = {
      family_name: familyName,
      family_code: familyCode,
      annual_charges: feeSummary.totalAnnualCharges,
      admission_fee: feeSummary.totalAdmissionFee,
      workbook_charges: feeSummary.totalWorkbookCharges,
      book_charges: feeSummary.totalBookCharges,
      monthly_fee: feeSummary.totalMonthlyFee,
      total_fee: totalFee,
      total_payable: totalPayable,
      discount: discountPercentage,
      final_total: finalTotalAmount,
      total_paid: paidAmount,
      last_pendings: lastPendings,
    };

    if (isTotalRecordUpdateMode && existingTotalRecord) {
      const result = await showUpdateConfirmation({
        familyCode,
        totalStudents,
        totalPayable,
        discountPercentage,
        finalTotal: finalTotalAmount,
        paidAmount,
        lastPending: lastPendings,
        formatCurrency,
      });
      if (!result.isConfirmed) return;
      await processUpdateSubmission(familyRecordData);
    } else {
      const result = await showSubmitConfirmation({
        familyCode,
        totalStudents,
        totalPayable,
        paidAmount,
        formatCurrency,
      });
      if (!result.isConfirmed) return;
      await processNewSubmission(familyRecordData);
    }
  };

  // ========== MODAL HANDLERS ==========
  const handleFamilyCheckUpdate = () => {
    setIsFamilyCheckOpen(false);
    handleFamilyCheckDialog("update");
  };
  const handleFamilyCheckView = () => {
    setIsFamilyCheckOpen(false);
    handleFamilyCheckDialog("view");
  };
  const handleFamilyCheckSubmitNew = () => {
    setIsFamilyCheckOpen(false);
    handleFamilyCheckDialog("submit_new");
  };
  const closeFamilyCheck = () => setIsFamilyCheckOpen(false);
  const closeFamilyDetails = () => setIsFamilyDetailsOpen(false);
  const closeStudentDetails = () => setIsStudentDetailsOpen(false);
  const closeFeeDetails = () => setIsFeeDetailsOpen(false);
  const closeFeeRecords = () => setIsFeeRecordsOpen(false);
  const closeUpdateSuccess = () => {
    setIsUpdateSuccessOpen(false);
    resetForm();
    setFamilyCode("");
  };

  // Show modals via state setters
  const showFamilyDetailsModal = () => {
    if (!familyData) {
      addWarningAlert("No family data available to display.");
      return;
    }
    setIsFamilyDetailsOpen(true);
  };

  const showStudentDetailsModal = (item) => {
    setSelectedStudentItem(item);
    setIsStudentDetailsOpen(true);
  };

  const showFeeDetailsModal = (item) => {
    setSelectedStudentItem(item);
    setIsFeeDetailsOpen(true);
  };

  const showFamilyFeeRecordsModal = (feeRecords) => {
    setFeeRecordsData(feeRecords);
    setIsFeeRecordsOpen(true);
  };

  return (
    <div className="ApplySoloStdFee-container">
      <Sidebar />

      <div className="ApplySoloStdFee-main-content">
        {/* ========== MODALS COMPONENT ========== */}
        <Modals
          // Family Check Dialog
          isFamilyCheckOpen={isFamilyCheckOpen}
          closeFamilyCheck={closeFamilyCheck}
          familyCheckResult={familyCheckResult}
          familyCode={familyCode}
          onFamilyCheckUpdate={handleFamilyCheckUpdate}
          onFamilyCheckView={handleFamilyCheckView}
          onFamilyCheckSubmitNew={handleFamilyCheckSubmitNew}
          // Family Details Modal
          isFamilyDetailsOpen={isFamilyDetailsOpen}
          closeFamilyDetails={closeFamilyDetails}
          students={students}
          totalStudents={totalStudents}
          feeSummary={feeSummary}
          formatCurrency={formatCurrency}
          // Student Details Modal
          isStudentDetailsOpen={isStudentDetailsOpen}
          closeStudentDetails={closeStudentDetails}
          studentItem={selectedStudentItem}
          // Fee Details Modal
          isFeeDetailsOpen={isFeeDetailsOpen}
          closeFeeDetails={closeFeeDetails}
          // Fee Records Modal
          isFeeRecordsOpen={isFeeRecordsOpen}
          closeFeeRecords={closeFeeRecords}
          feeRecords={feeRecordsData}
          onUpdateExisting={() => handleFamilyCheckDialog("update")}
          // Update Success Modal
          isUpdateSuccessOpen={isUpdateSuccessOpen}
          closeUpdateSuccess={closeUpdateSuccess}
          lastPending={lastPending}
        />

        {/* Alert Container */}
        {alerts.length > 0 && (
          <div className="alert-container">
            {alerts.map((alert) => (
              <div
                key={alert.id}
                className={`alert alert-${alert.type} ${
                  alert.exiting ? "exiting" : ""
                }`}
              >
                <div className="alert-icon">
                  {alert.type === "success" && <FaCheckCircle />}
                  {alert.type === "error" && <FaTimesCircle />}
                  {alert.type === "warning" && <FaExclamationTriangle />}
                  {alert.type === "info" && <FaInfoCircle />}
                  {alert.type === "loading" && <FaSpinner />}
                </div>

                <div className="alert-content">
                  <div className="alert-header">
                    <div className="alert-title">
                      {alert.type === "success" && "Success"}
                      {alert.type === "error" && "Error"}
                      {alert.type === "warning" && "Warning"}
                      {alert.type === "info" && "Information"}
                      {alert.type === "loading" && "Processing"}
                    </div>
                    <div className="alert-time">
                      {moment(alert.timestamp).format("HH:mm:ss")}
                    </div>
                  </div>

                  <p className="alert-message">{alert.message}</p>
                </div>

                <button
                  className="alert-close"
                  onClick={() => removeAlert(alert.id)}
                  title="Dismiss alert"
                >
                  <FaTimes />
                </button>

                {alert.type !== "loading" && alert.duration > 0 && (
                  <div
                    className="alert-progress"
                    style={{
                      animationDuration: `${alert.duration}ms`,
                      animationPlayState: alert.exiting ? "paused" : "running",
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Alert History Button */}
        {alerts.length > 0 && (
          <button
            className="alert-history-btn"
            onClick={() => setShowAlertHistory(!showAlertHistory)}
            title="View alert history"
          >
            <FaBell />
            {alertHistory.length > 0 && (
              <span className="alert-history-badge">
                {Math.min(alertHistory.length, 99)}
              </span>
            )}
          </button>
        )}

        {/* Alert History Panel */}
        {showAlertHistory && (
          <div className="alert-history-panel">
            <div className="alert-history-header">
              <div className="alert-history-title">
                <FaHistory />
                Alert History ({alertHistory.length})
              </div>
              <div className="alert-history-actions">
                <button
                  className="alert-close"
                  onClick={clearAlertHistory}
                  title="Clear history"
                  style={{ color: "white", marginRight: "8px" }}
                >
                  <FaTimes />
                </button>
                <button
                  className="alert-close"
                  onClick={() => setShowAlertHistory(false)}
                  title="Close history"
                  style={{ color: "white" }}
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            <div className="alert-history-content">
              {alertHistory.length > 0 ? (
                alertHistory.map((alert) => (
                  <div
                    key={alert.id}
                    className="alert-history-item"
                    onClick={() => {
                      addAlert(alert.message, alert.type, alert.duration);
                      setShowAlertHistory(false);
                    }}
                  >
                    <div className="alert-history-item-time">
                      {moment(alert.timestamp).format("YYYY-MM-DD HH:mm:ss")}
                    </div>
                    <div className="alert-history-item-message">
                      <div className={`alert-icon alert-${alert.type}`}>
                        {alert.type === "success" && <FaCheckCircle />}
                        {alert.type === "error" && <FaTimesCircle />}
                        {alert.type === "warning" && <FaExclamationTriangle />}
                        {alert.type === "info" && <FaInfoCircle />}
                        {alert.type === "loading" && <FaSpinner />}
                      </div>
                      {alert.message}
                    </div>
                  </div>
                ))
              ) : (
                <div className="alert-history-empty">
                  <FaHistory
                    style={{
                      fontSize: "48px",
                      opacity: 0.3,
                      marginBottom: "16px",
                    }}
                  />
                  <p>No alert history yet</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Debug Panel */}
        {debugMode && (
          <div className="ApplySoloStdFee-debugPanel">
            <div className="ApplySoloStdFee-debugHeader">
              <FaBug className="ApplySoloStdFee-debugIcon" />
              <h3>Debug Panel</h3>
              <div className="ApplySoloStdFee-debugActions">
                <button
                  className="ApplySoloStdFee-debugActionBtn"
                  onClick={clearApiLogs}
                  title="Clear Logs"
                >
                  <FaTimes />
                </button>
                <button
                  className="ApplySoloStdFee-debugActionBtn"
                  onClick={testApiConnection}
                  title="Test Connection"
                >
                  <FaSync />
                </button>
                <button
                  className="ApplySoloStdFee-debugClose"
                  onClick={() => setDebugMode(false)}
                  title="Close Debug Panel"
                >
                  <FaTimes />
                </button>
              </div>
            </div>
            <div className="ApplySoloStdFee-debugContent">
              <div className="ApplySoloStdFee-debugSection">
                <h4>
                  System Status:{" "}
                  <span className={`ApplySoloStdFee-status-${systemStatus}`}>
                    {systemStatus.replace("_", " ")}
                  </span>
                </h4>
                <h4>
                  Family Code: <strong>{familyCode || "Not entered"}</strong>
                </h4>
                <h4>
                  Family Fee Exists:{" "}
                  <strong>{familyFeeExists ? "Yes" : "No"}</strong>
                </h4>
                <h4>
                  Students Found: <strong>{totalStudents}</strong>
                </h4>
                <h4>
                  API Endpoint:{" "}
                  <code style={{ fontSize: "12px" }}>{apiEndpoint}</code>
                </h4>
              </div>
              <div className="ApplySoloStdFee-debugSection">
                <h4>Family Existence Check</h4>
                <pre className="ApplySoloStdFee-debugPre">
                  {JSON.stringify(familyExistenceCheck, null, 2)}
                </pre>
              </div>
              <div className="ApplySoloStdFee-debugSection">
                <h4>API Logs ({apiLogs.length})</h4>
                <div className="ApplySoloStdFee-apiLogs">
                  {apiLogs.slice(0, 10).map((log) => (
                    <div
                      key={log.id}
                      className={`ApplySoloStdFee-apiLog ApplySoloStdFee-apiLog-${log.status}`}
                    >
                      <div className="ApplySoloStdFee-apiLog-time">
                        {moment(log.timestamp).format("HH:mm:ss")}
                      </div>
                      <div className="ApplySoloStdFee-apiLog-action">
                        {log.action}
                      </div>
                      <div className="ApplySoloStdFee-apiLog-status">
                        {log.status}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <header className="ApplySoloStdFee-header">
          <div className="ApplySoloStdFee-headerContent">
            <h1 className="ApplySoloStdFee-title" style={{color:'white'}}>
              <FaUsers className="ApplySoloStdFee-titleIcon" />
              Family Fee Management
            </h1>
            <p className="ApplySoloStdFee-subtitle">
              Apply fees and expenses for family students
            </p>
            <div className="ApplySoloStdFee-systemInfo">
              <span
                className={`ApplySoloStdFee-systemStatus ApplySoloStdFee-systemStatus-${systemStatus}`}
              >
                {systemStatus.replace("_", " ")}
              </span>
              <span className="ApplySoloStdFee-systemTime">
                {moment().format("DD-MM-YYYY HH:mm")}
              </span>
              <span className="ApplySoloStdFee-apiEndpoint">
                API: {apiEndpoint.split("/").pop()}
              </span>
            </div>
          </div>
          <div className="ApplySoloStdFee-headerActions">
            {alerts.length > 0 && (
              <div className="ApplySoloStdFee-alertBadge">
                <FaBell />
                <span>{alerts.length}</span>
              </div>
            )}
            <button
              className="ApplySoloStdFee-debugBtn"
              onClick={() => setDebugMode(!debugMode)}
              title="Toggle Debug Mode"
            >
              <FaBug />
            </button>
            <button
              className="ApplySoloStdFee-testBtn"
              onClick={testApiConnection}
              title="Test API Connection"
            >
              <FaDatabase />
            </button>
          </div>
        </header>

        {/* Search Section */}
        <section className="ApplySoloStdFee-searchSection">
          <div className="ApplySoloStdFee-searchCard">
            <div className="ApplySoloStdFee-searchHeader">
              <FaSearch className="ApplySoloStdFee-searchIcon" />
              <h2>Search Family</h2>
              <div className="ApplySoloStdFee-classBadge">{className}</div>
            </div>

            <div className="ApplySoloStdFee-searchForm">
              <div className="ApplySoloStdFee-idInputGroup">
                <label className="ApplySoloStdFee-idLabel">
                  <FaIdCard className="ApplySoloStdFee-idLabelIcon" />
                  Enter Family Code
                </label>

                <div className="family-input-group" style={{ width: "70%" }}>
                  <input
                    type="text"
                    value={familyCode}
                    onChange={handleFamilyCodeChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter family code (e.g., FAM-001)"
                    className="family-search-input"
                    disabled={isLoading}
                    ref={inputRef}
                  />
                  {familyCodeSuggestions.length > 0 && (
                    <div
                      className="family-suggestion-list"
                      style={{ width: "85%" }}
                    >
                      {familyCodeSuggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          className="family-suggestion-item"
                          onClick={() =>
                            handleSuggestionClick(suggestion, "familyCode")
                          }
                        >
                          <FiCreditCard className="family-suggestion-icon" />
                          <div>
                            <div className="family-suggestion-main">
                              {suggestion.family_code}
                            </div>
                            <div className="family-suggestion-sub">
                              {suggestion.family_name}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="ApplySoloStdFee-idHint">
                  Format: Family Code (e.g., F-DEA101, F-DEA202)
                </div>
              </div>

              {searchError && (
                <div className="ApplySoloStdFee-searchError">
                  <FaExclamationTriangle className="ApplySoloStdFee-errorIcon" />
                  <span>{searchError}</span>
                </div>
              )}

              <div className="ApplySoloStdFee-searchActions">
                <button
                  className="ApplySoloStdFee-searchBtn"
                  onClick={handleSearch}
                  disabled={
                    isLoading || !familyCode || familyCode.trim() === ""
                  }
                >
                  {isLoading ? (
                    <>
                      <div className="ApplySoloStdFee-loading-small"></div>
                      Searching...
                    </>
                  ) : (
                    <>
                      <FaSearch className="ApplySoloStdFee-searchBtnIcon" />
                      Search Family
                    </>
                  )}
                </button>

                <button
                  className="ApplySoloStdFee-forgetBtn"
                  onClick={() => navigate("/Forget-Family-Code")}
                >
                  <FaSearch className="ApplySoloStdFee-forgetIcon" />
                  Forgot Family Code?
                </button>

                <button
                  className="ApplySoloStdFee-clearBtn"
                  onClick={handleClearForm}
                  disabled={isLoading}
                >
                  <FaTimes className="ApplySoloStdFee-clearIcon" />
                  Clear
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Admission Information Banner */}
        {familyCode && familyData && (
          <div
            style={{
              backgroundColor: "#f0f9ff",
              border: "1px solid #bae6fd",
              borderRadius: "8px",
              padding: "16px",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <FaInfoCircle style={{ fontSize: "24px", color: "#0369a1" }} />
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontWeight: "bold",
                  marginBottom: "4px",
                  color: "#0369a1",
                }}
              >
                Admission Period Information
              </div>
              <div style={{ fontSize: "14px", color: "#475569" }}>
                School admission opens in{" "}
                {getMonthName(SCHOOL_CONFIG.admissionOpeningMonth)}. Student
                joining in {getMonthName(currentMonth)}. Fee period starts from{" "}
                {getMonthName(startMonth.getMonth())} {startMonth.getFullYear()}
                .
              </div>
            </div>
            <button
              onClick={() => {
                setStartMonth(
                  getAdmissionStartMonth(SCHOOL_CONFIG.admissionOpeningMonth)
                );
                addInfoAlert(
                  `Start month reset to admission opening month (${getMonthName(
                    SCHOOL_CONFIG.admissionOpeningMonth
                  )})`
                );
              }}
              style={{
                padding: "8px 16px",
                backgroundColor: "#0369a1",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              Reset to Admission Month
            </button>
          </div>
        )}

        {/* Family Existence Check Indicator */}
        {familyExistenceCheck && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              padding: "16px",
              borderRadius: "8px",
              marginBottom: "16px",
              gap: "12px",
              ...(familyExistenceCheck.exists
                ? {
                    backgroundColor: "#fff3e0",
                    border: "1px solid #ff9800",
                    color: "#e65100",
                  }
                : {
                    backgroundColor: "#e8f5e9",
                    border: "1px solid #4caf50",
                    color: "#2e7d32",
                  }),
            }}
          >
            {familyExistenceCheck.exists ? (
              <>
                <FaExclamationTriangle
                  style={{
                    fontSize: "24px",
                    flexShrink: 0,
                    color: "#ff9800",
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                    Family fee record already exists!
                  </div>
                  <div style={{ marginBottom: "8px" }}>
                    {familyExistenceCheck.message}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      opacity: 0.8,
                    }}
                  >
                    Found {familyExistenceCheck.data?.fee_record_count || 0}{" "}
                    existing fee records for{" "}
                    {familyExistenceCheck.data?.student_count_in_family || 0}{" "}
                    students
                  </div>
                </div>
                <button
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 16px",
                    backgroundColor: "#ff9800",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontWeight: "500",
                    transition: "background-color 0.2s",
                    flexShrink: 0,
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = "#f57c00")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = "#ff9800")
                  }
                  onClick={() => setIsFamilyCheckOpen(true)}
                >
                  <FaArrowUp />
                  View Options
                </button>
              </>
            ) : (
              <>
                <FaCheckCircle
                  style={{
                    fontSize: "24px",
                    flexShrink: 0,
                    color: "#4caf50",
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                    New family submission ready
                  </div>
                  <div style={{ marginBottom: "8px" }}>
                    {familyExistenceCheck.message}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      opacity: 0.8,
                    }}
                  >
                    Ready to create new fee record for{" "}
                    {familyExistenceCheck.data?.student_count_in_family || 0}{" "}
                    students
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Update Mode Indicator */}
        {isTotalRecordUpdateMode && familyCode && (
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              padding: "16px",
              borderRadius: "8px",
              marginBottom: "16px",
              gap: "12px",
              backgroundColor: "#fff3cd",
              border: "1px solid #ffc107",
              color: "#856404",
            }}
          >
            <FaExclamationTriangle
              style={{
                fontSize: "24px",
                flexShrink: 0,
                color: "#ffc107",
              }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ marginBottom: "8px" }}>
                <strong>Update Mode:</strong> Family fee record already exists
                in the system. You can update the existing record.
              </div>
              <div
                style={{
                  fontSize: "14px",
                  opacity: 0.8,
                  padding: "8px",
                  backgroundColor: "rgba(255, 193, 7, 0.1)",
                  borderRadius: "4px",
                }}
              >
                Family Code: {familyCode} | Record ID: {existingTotalRecord?.id}{" "}
                | Created:{" "}
                {moment(existingTotalRecord?.created_at).format("DD-MM-YYYY")}
              </div>
            </div>
          </div>
        )}

        {/* Family Info Card */}
        {familyCode && familyData && (
          <section className="ApplySoloStdFee-studentSection">
            <div className="ApplySoloStdFee-studentCard">
              <div className="ApplySoloStdFee-studentCardHeader">
                <FaUsers className="ApplySoloStdFee-studentIcon" />
                <h3>Family Information</h3>
                <div className="ApplySoloStdFee-studentBadge">
                  {totalStudents} Student{totalStudents !== 1 ? "s" : ""}
                </div>
                <button
                  className="ApplySoloStdFee-viewDetailsBtn"
                  onClick={showFamilyDetailsModal}
                  style={{ marginLeft: "auto", marginRight: "10px" }}
                >
                  <>
                    <FaEye className="ApplySoloStdFee-viewIcon" />
                    View Family Details
                  </>
                </button>
              </div>

              <div className="ApplySoloStdFee-studentInfo">
                <div className="ApplySoloStdFee-studentRow">
                  <div className="ApplySoloStdFee-studentField">
                    <FaIdCard className="ApplySoloStdFee-fieldIcon" />
                    <div>
                      <div className="ApplySoloStdFee-fieldLabel">
                        Family Code
                      </div>
                      <div className="ApplySoloStdFee-fieldValue">
                        {familyCode}
                      </div>
                    </div>
                  </div>

                  <div className="ApplySoloStdFee-studentField">
                    <FaUsers className="ApplySoloStdFee-fieldIcon" />
                    <div>
                      <div className="ApplySoloStdFee-fieldLabel">
                        Total Students
                      </div>
                      <div className="ApplySoloStdFee-fieldValue">
                        {totalStudents}
                      </div>
                    </div>
                  </div>

                  <div className="ApplySoloStdFee-studentField">
                    <FaMoneyBillWave className="ApplySoloStdFee-fieldIcon" />
                    <div>
                      <div className="ApplySoloStdFee-fieldLabel">
                        Total Monthly Fee
                      </div>
                      <div className="ApplySoloStdFee-fieldValue">
                        {formatCurrency(feeSummary.totalMonthlyFee)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="ApplySoloStdFee-studentActions">
                  <div className="ApplySoloStdFee-studentQuickStats">
                    <div className="ApplySoloStdFee-quickStat">
                      <span>Total Payable</span>
                      <strong>{formatCurrency(feeSummary.totalPayable)}</strong>
                    </div>
                    <div className="ApplySoloStdFee-quickStat">
                      <span>Discount</span>
                      <strong>{discountPercentage}%</strong>
                    </div>
                    <div className="ApplySoloStdFee-quickStat">
                      <span>Status</span>
                      <strong
                        className={
                          lastPending > 0
                            ? "ApplySoloStdFee-status-pending"
                            : "ApplySoloStdFee-status-paid"
                        }
                      >
                        {lastPending > 0 ? "Pending" : "Paid"}
                      </strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Students List */}
        {students.length > 0 && (
          <section
            style={{
              marginBottom: "2rem",
              animation:
                "fadeInUp 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.3s both",
              position: "relative",
            }}
          >
            <div
              style={{
                background: "#ffffff",
                borderRadius: "1rem",
                padding: "2rem",
                boxShadow:
                  "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                border: "1px solid #e5e7eb",
                position: "relative",
                overflow: "hidden",
                transition: "all 250ms cubic-bezier(0.4, 0, 0.2, 1)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 25px 50px -12px rgba(0, 0, 0, 0.25)";
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  background:
                    "linear-gradient(90deg, #10b981 0%, #3b82f6 33%, #8b5cf6 66%, #ec4899 100%)",
                  borderRadius: "1rem 1rem 0 0",
                  zIndex: 1,
                }}
              />

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  marginBottom: "1.5rem",
                  paddingBottom: "1rem",
                  borderBottom: "1px solid #e5e7eb",
                  position: "relative",
                  zIndex: 2,
                }}
              >
                <div
                  style={{
                    fontSize: "2rem",
                    color: "#2563eb",
                    background:
                      "linear-gradient(135deg, rgba(37, 99, 235, 0.1), rgba(16, 185, 129, 0.1))",
                    padding: "1rem",
                    borderRadius: "0.75rem",
                    border: "1px solid rgba(37, 99, 235, 0.2)",
                    boxShadow:
                      "inset 0 2px 4px rgba(255, 255, 255, 0.5), 0 2px 8px rgba(37, 99, 235, 0.1)",
                  }}
                >
                  <FaUserGraduate />
                </div>

                <h3
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: 800,
                    color: "#111827",
                    flex: 1,
                    letterSpacing: "-0.025em",
                    background:
                      "linear-gradient(135deg, #111827 0%, #374151 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Students in Family
                </h3>

                <span
                  style={{
                    background: "linear-gradient(135deg, #2563eb, #8b5cf6)",
                    color: "white",
                    padding: "0.5rem 1.5rem",
                    borderRadius: "9999px",
                    fontSize: "0.875rem",
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
                    position: "relative",
                    overflow: "hidden",
                    transition: "all 150ms cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.05)";
                    e.currentTarget.style.boxShadow =
                      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.2)";
                  }}
                >
                  {totalStudents} Student{totalStudents !== 1 ? "s" : ""}
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.75rem",
                  maxHeight: "500px",
                  overflowY: "auto",
                  paddingRight: "0.5rem",
                  marginTop: "0.5rem",
                }}
              >
                {students.map((item, index) => {
                  const student = item.student;
                  const feeDetails = item.feeDetails || {};
                  return (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "1.25rem",
                        background: "#f9fafb",
                        borderRadius: "0.75rem",
                        border: "1px solid #e5e7eb",
                        transition: "all 150ms cubic-bezier(0.4, 0, 0.2, 1)",
                        animation: `slideInRight 0.3s ease-out ${
                          0.1 * (index + 1)
                        }s both`,
                        position: "relative",
                        overflow: "hidden",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateX(8px)";
                        e.currentTarget.style.borderColor = "#60a5fa";
                        e.currentTarget.style.boxShadow =
                          "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05), inset 0 0 0 1px rgba(37, 99, 235, 0.1)";
                        e.currentTarget.style.background =
                          "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateX(0)";
                        e.currentTarget.style.borderColor = "#e5e7eb";
                        e.currentTarget.style.boxShadow = "none";
                        e.currentTarget.style.background = "#f9fafb";
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: "4px",
                          background:
                            "linear-gradient(180deg, #2563eb 0%, #8b5cf6 100%)",
                          transform: "scaleY(0)",
                          transition:
                            "transform 250ms cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "scaleY(1)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scaleY(0)";
                        }}
                      />

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "1rem",
                          flex: 1,
                          minWidth: 0,
                          zIndex: 2,
                        }}
                      >
                        <div
                          className="student-avatar"
                          style={{
                            width: "56px",
                            height: "56px",
                            background:
                              "linear-gradient(135deg, #2563eb 0%, #8b5cf6 50%, #ef4444 100%)",
                            color: "white",
                            borderRadius: "0.75rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "1.5rem",
                            fontWeight: 800,
                            flexShrink: 0,
                            boxShadow:
                              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), inset 0 2px 4px rgba(255, 255, 255, 0.3)",
                            transition:
                              "all 250ms cubic-bezier(0.4, 0, 0.2, 1)",
                            position: "relative",
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background:
                                "linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%)",
                              backgroundSize: "200% 200%",
                              animation: "shimmer 3s infinite linear",
                            }}
                          />
                          {student.name ? student.name.charAt(0) : "S"}
                        </div>

                        <div
                          style={{
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          <div
                            style={{
                              fontSize: "1.125rem",
                              fontWeight: 700,
                              color: "#111827",
                              marginBottom: "0.5rem",
                              display: "flex",
                              alignItems: "center",
                              gap: "0.75rem",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              transition:
                                "color 150ms cubic-bezier(0.4, 0, 0.2, 1)",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = "#2563eb";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = "#111827";
                            }}
                          >
                            {student.name || "Student"}
                            <span
                              className="student-id"
                              style={{
                                fontSize: "0.875rem",
                                color: "#6b7280",
                                fontWeight: 500,
                                background: "#f3f4f6",
                                padding: "0.25rem 0.75rem",
                                borderRadius: "9999px",
                                fontFamily:
                                  "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
                                border: "1px solid #e5e7eb",
                                transition:
                                  "all 150ms cubic-bezier(0.4, 0, 0.2, 1)",
                              }}
                            >
                              ({student.student_id || "N/A"})
                            </span>
                          </div>

                          <div
                            className="student-class"
                            style={{
                              fontSize: "0.875rem",
                              color: "#4b5563",
                              marginBottom: "0.75rem",
                              fontWeight: 500,
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              padding: "0.5rem",
                              background: "rgba(255, 255, 255, 0.5)",
                              borderRadius: "0.375rem",
                              border: "1px solid rgba(0, 0, 0, 0.05)",
                              transition:
                                "all 150ms cubic-bezier(0.4, 0, 0.2, 1)",
                            }}
                          >
                            📚 {student.class || "N/A"} -{" "}
                            {student.section || "N/A"}
                          </div>

                          <div
                            className="student-fee"
                            style={{
                              fontSize: "1rem",
                              color: "#10b981",
                              fontWeight: 600,
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              padding: "0.75rem",
                              background:
                                "linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.1) 100%)",
                              borderRadius: "0.5rem",
                              border: "1px solid rgba(16, 185, 129, 0.2)",
                              transition:
                                "all 150ms cubic-bezier(0.4, 0, 0.2, 1)",
                              position: "relative",
                              overflow: "hidden",
                            }}
                          >
                            <span>Monthly Fee:</span>
                            <span style={{ fontWeight: 700 }}>
                              PKR Rs.
                              {parseFloat(
                                feeDetails.monthly_fee || 0
                              ).toLocaleString("en-PK", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                            <div
                              style={{
                                position: "absolute",
                                top: 0,
                                left: "-100%",
                                width: "100%",
                                height: "100%",
                                background:
                                  "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)",
                                transition: "left 700ms ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.left = "100%";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.left = "-100%";
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: "0.5rem",
                          flexShrink: 0,
                          zIndex: 2,
                        }}
                      >
                        <button
                          style={{
                            width: "44px",
                            height: "44px",
                            borderRadius: "0.5rem",
                            border: "1px solid #e5e7eb",
                            background: "#ffffff",
                            color: "#6b7280",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            fontSize: "1.125rem",
                            transition:
                              "all 150ms cubic-bezier(0.4, 0, 0.2, 1)",
                            position: "relative",
                            overflow: "hidden",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#f3f4f6";
                            e.currentTarget.style.borderColor = "#d1d5db";
                            e.currentTarget.style.color = "#111827";
                            e.currentTarget.style.transform =
                              "translateY(-2px) scale(1.1)";
                            e.currentTarget.style.boxShadow =
                              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(37, 99, 235, 0.2)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#ffffff";
                            e.currentTarget.style.borderColor = "#e5e7eb";
                            e.currentTarget.style.color = "#6b7280";
                            e.currentTarget.style.transform =
                              "translateY(0) scale(1)";
                            e.currentTarget.style.boxShadow = "none";
                          }}
                          onMouseDown={(e) => {
                            e.currentTarget.style.transform =
                              "translateY(0) scale(0.95)";
                            e.currentTarget.style.transition = "all 100ms ease";
                          }}
                          onMouseUp={(e) => {
                            e.currentTarget.style.transform =
                              "translateY(-2px) scale(1.1)";
                          }}
                          onClick={() => showStudentDetailsModal(item)}
                        >
                          <FaEye />
                        </button>

                        <button
                          style={{
                            width: "44px",
                            height: "44px",
                            borderRadius: "0.5rem",
                            border: "1px solid #e5e7eb",
                            background: "#ffffff",
                            color: "#6b7280",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            fontSize: "1.125rem",
                            transition:
                              "all 150ms cubic-bezier(0.4, 0, 0.2, 1)",
                            position: "relative",
                            overflow: "hidden",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background =
                              "rgba(16, 185, 129, 0.05)";
                            e.currentTarget.style.borderColor =
                              "rgba(16, 185, 129, 0.3)";
                            e.currentTarget.style.color = "#10b981";
                            e.currentTarget.style.transform =
                              "translateY(-2px) scale(1.1)";
                            e.currentTarget.style.boxShadow =
                              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(16, 185, 129, 0.2)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#ffffff";
                            e.currentTarget.style.borderColor = "#e5e7eb";
                            e.currentTarget.style.color = "#6b7280";
                            e.currentTarget.style.transform =
                              "translateY(0) scale(1)";
                            e.currentTarget.style.boxShadow = "none";
                          }}
                          onMouseDown={(e) => {
                            e.currentTarget.style.transform =
                              "translateY(0) scale(0.95)";
                            e.currentTarget.style.transition = "all 100ms ease";
                          }}
                          onMouseUp={(e) => {
                            e.currentTarget.style.transform =
                              "translateY(-2px) scale(1.1)";
                          }}
                          onClick={() => showFeeDetailsModal(item)}
                        >
                          <FaMoneyBillWave />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Family Fee Application Form */}
        {familyCode && familyData && (
          <section className="ApplySoloStdFee-formSection">
            <div className="ApplySoloStdFee-formCard">
              <div className="ApplySoloStdFee-formHeader">
                <FaMoneyBillWave className="ApplySoloStdFee-formIcon" />
                <h2>
                  {isTotalRecordUpdateMode
                    ? "Update Family Fee Record"
                    : "Submit New Family Fee Record"}
                </h2>
                <div className="ApplySoloStdFee-formModeBadge">
                  {isTotalRecordUpdateMode ? (
                    <>
                      <FaEdit className="ApplySoloStdFee-modeIcon" />
                      Update Mode
                    </>
                  ) : (
                    <>
                      <FaPaperPlane className="ApplySoloStdFee-modeIcon" />
                      Submit Mode
                    </>
                  )}
                </div>
                <button
                  className="ApplySoloStdFee-clearFormBtn"
                  onClick={handleClearForm}
                >
                  <FaTimes className="ApplySoloStdFee-clearIcon" />
                  Clear Form
                </button>
                <button
                  className="ApplySoloStdFee-debugInfoBtn"
                  onClick={showDebugPanel}
                >
                  <FaBug className="ApplySoloStdFee-debugIcon" />
                  Debug Info
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                {/* One-Time Charges - NOW EDITABLE */}
                <div className="ApplySoloStdFee-formSectionGroup">
                  <h3 className="ApplySoloStdFee-formSubtitle">
                    <FaCreditCard className="ApplySoloStdFee-subtitleIcon" />
                    Family One-Time Charges (Total) - Editable for Poor Students
                  </h3>

                  <div className="ApplySoloStdFee-formGrid">
                    <div className="ApplySoloStdFee-formGroup">
                      <label className="ApplySoloStdFee-formLabel">
                        <FaMoneyBillWave className="ApplySoloStdFee-labelIcon" />
                        Annual Charges
                      </label>
                      <input
                        type="number"
                        className="ApplySoloStdFee-formInput"
                        value={feeSummary.totalAnnualCharges}
                        onChange={(e) =>
                          handleOneTimeChargeChange(
                            "totalAnnualCharges",
                            e.target.value
                          )
                        }
                        min="0"
                        step="0.01"
                        placeholder="Enter amount"
                      />
                      <div className="ApplySoloStdFee-fieldHint">
                        Adjust for poor students
                      </div>
                    </div>

                    <div className="ApplySoloStdFee-formGroup">
                      <label className="ApplySoloStdFee-formLabel">
                        <FaMoneyBillWave className="ApplySoloStdFee-labelIcon" />
                        Admission Fee
                      </label>
                      <input
                        type="number"
                        className="ApplySoloStdFee-formInput"
                        value={feeSummary.totalAdmissionFee}
                        onChange={(e) =>
                          handleOneTimeChargeChange(
                            "totalAdmissionFee",
                            e.target.value
                          )
                        }
                        min="0"
                        step="0.01"
                        placeholder="Enter amount"
                      />
                      <div className="ApplySoloStdFee-fieldHint">
                        Adjust for poor students
                      </div>
                    </div>

                    <div className="ApplySoloStdFee-formGroup">
                      <label className="ApplySoloStdFee-formLabel">
                        <FaMoneyBillWave className="ApplySoloStdFee-labelIcon" />
                        Workbook Charges
                      </label>
                      <input
                        type="number"
                        className="ApplySoloStdFee-formInput"
                        value={feeSummary.totalWorkbookCharges}
                        onChange={(e) =>
                          handleOneTimeChargeChange(
                            "totalWorkbookCharges",
                            e.target.value
                          )
                        }
                        min="0"
                        step="0.01"
                        placeholder="Enter amount"
                      />
                      <div className="ApplySoloStdFee-fieldHint">
                        Adjust for poor students
                      </div>
                    </div>

                    <div className="ApplySoloStdFee-formGroup">
                      <label className="ApplySoloStdFee-formLabel">
                        <FaMoneyBillWave className="ApplySoloStdFee-labelIcon" />
                        Book Charges
                      </label>
                      <input
                        type="number"
                        className="ApplySoloStdFee-formInput"
                        value={feeSummary.totalBookCharges}
                        onChange={(e) =>
                          handleOneTimeChargeChange(
                            "totalBookCharges",
                            e.target.value
                          )
                        }
                        min="0"
                        step="0.01"
                        placeholder="Enter amount"
                      />
                      <div className="ApplySoloStdFee-fieldHint">
                        Adjust for poor students
                      </div>
                    </div>
                  </div>
                </div>

                {/* Monthly Fee Period */}
                <div className="ApplySoloStdFee-formSectionGroup">
                  <h3 className="ApplySoloStdFee-formSubtitle">
                    <FaCalendarAlt className="ApplySoloStdFee-subtitleIcon" />
                    Family Monthly Fee Period
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#6b7280",
                        fontWeight: "normal",
                        marginLeft: "8px",
                      }}
                    >
                      (Admission opens in{" "}
                      {getMonthName(SCHOOL_CONFIG.admissionOpeningMonth)})
                    </span>
                  </h3>

                  <div className="ApplySoloStdFee-formRow">
                    <div className="ApplySoloStdFee-formGroup">
                      <label className="ApplySoloStdFee-formLabel">
                        <FaMoneyBillWave className="ApplySoloStdFee-labelIcon" />
                        Total Monthly Fee
                      </label>
                      <input
                        type="number"
                        className="ApplySoloStdFee-formInput"
                        value={feeSummary.totalMonthlyFee}
                        onChange={(e) => handleMonthlyFeeChange(e.target.value)}
                        min="0"
                        step="0.01"
                        placeholder="Enter amount"
                      />
                      <div className="ApplySoloStdFee-fieldHint">
                        Adjust monthly fee if needed
                      </div>
                    </div>

                    <div className="ApplySoloStdFee-formGroup">
                      <label className="ApplySoloStdFee-formLabel">
                        <FaCalendarAlt className="ApplySoloStdFee-labelIcon" />
                        Start Month
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            fontWeight: "normal",
                            marginLeft: "8px",
                          }}
                        >
                          (Admission opens in{" "}
                          {getMonthName(SCHOOL_CONFIG.admissionOpeningMonth)})
                        </span>
                      </label>
                      <DatePicker
                        selected={startMonth}
                        onChange={(date) => {
                          if (validateStartMonth(date)) {
                            setStartMonth(date);
                            addInfoAlert(
                              `Start month changed to ${getMonthName(
                                date.getMonth()
                              )} ${date.getFullYear()}`
                            );
                          }
                        }}
                        dateFormat="MMMM yyyy"
                        showMonthYearPicker
                        className="ApplySoloStdFee-datePicker"
                        minDate={
                          new Date(
                            currentYear - 1,
                            SCHOOL_CONFIG.admissionOpeningMonth,
                            1
                          )
                        }
                        maxDate={new Date(currentYear, currentMonth, 1)}
                        popperPlacement="bottom-start"
                      />
                      <div className="ApplySoloStdFee-fieldHint">
                        Change if student joined after admission period. Default
                        is admission opening month.
                      </div>
                    </div>

                    <div className="ApplySoloStdFee-formGroup">
                      <label className="ApplySoloStdFee-formLabel">
                        <FaCalendarAlt className="ApplySoloStdFee-labelIcon" />
                        End Month
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            fontWeight: "normal",
                            marginLeft: "8px",
                          }}
                        >
                          (Academic year ends in{" "}
                          {getMonthName(SCHOOL_CONFIG.academicYearEndMonth)})
                        </span>
                      </label>
                      <DatePicker
                        selected={endMonth}
                        onChange={(date) => setEndMonth(date)}
                        dateFormat="MMMM yyyy"
                        showMonthYearPicker
                        className="ApplySoloStdFee-datePicker"
                        minDate={startMonth}
                        maxDate={
                          new Date(
                            new Date().setFullYear(new Date().getFullYear() + 2)
                          )
                        }
                      />
                      <div className="ApplySoloStdFee-fieldHint">
                        Default is February of next academic year
                      </div>
                    </div>

                    <div className="ApplySoloStdFee-formGroup">
                      <label className="ApplySoloStdFee-formLabel">
                        <FaCalculator className="ApplySoloStdFee-labelIcon" />
                        Total Fee ({monthCount} months)
                      </label>
                      <input
                        type="text"
                        className="ApplySoloStdFee-formInput"
                        value={formatCurrency(totalFee)}
                        readOnly
                      />
                      <div className="ApplySoloStdFee-fieldHint">
                        Calculated based on selected period
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Summary */}
                <div className="ApplySoloStdFee-formSectionGroup">
                  <h3 className="ApplySoloStdFee-formSubtitle">
                    <FaCalculator className="ApplySoloStdFee-subtitleIcon" />
                    Family Payment Summary
                  </h3>

                  <div className="ApplySoloStdFee-paymentGrid">
                    <div className="ApplySoloStdFee-paymentCard">
                      <div className="ApplySoloStdFee-paymentHeader">
                        <FaMoneyBillWave className="ApplySoloStdFee-paymentIcon" />
                        <h4>Fee Breakdown</h4>
                      </div>

                      <div className="ApplySoloStdFee-paymentDetails">
                        <div className="ApplySoloStdFee-paymentRow">
                          <span>Total One-Time Charges</span>
                          <strong>
                            {formatCurrency(summary.oneTimeCharges)}
                          </strong>
                        </div>

                        <div className="ApplySoloStdFee-paymentRow">
                          <span>Total Monthly Fee ({monthCount} months)</span>
                          <strong>{formatCurrency(totalFee)}</strong>
                        </div>

                        <div className="ApplySoloStdFee-paymentRow ApplySoloStdFee-paymentTotal">
                          <span>Total Payable</span>
                          <strong>
                            {formatCurrency(summary.totalPayable)}
                          </strong>
                        </div>
                      </div>
                    </div>

                    <div className="ApplySoloStdFee-paymentCard">
                      <div className="ApplySoloStdFee-paymentHeader">
                        <FaPercent className="ApplySoloStdFee-paymentIcon" />
                        <h4>Discount & Payment</h4>
                      </div>

                      <div className="ApplySoloStdFee-paymentForm">
                        <div className="ApplySoloStdFee-formGroup">
                          <label className="ApplySoloStdFee-formLabel">
                            Discount (%)
                          </label>
                          <input
                            type="number"
                            className="ApplySoloStdFee-formInput"
                            value={discountPercentage}
                            onChange={(e) =>
                              handleDiscountChange(e.target.value)
                            }
                            min="0"
                            max="100"
                            step="0.1"
                            placeholder="0-100%"
                          />
                        </div>

                        <div className="ApplySoloStdFee-formGroup">
                          <label className="ApplySoloStdFee-formLabel">
                            Discount Amount
                          </label>
                          <input
                            type="text"
                            className="ApplySoloStdFee-formInput"
                            value={formatCurrency(summary.discountAmount)}
                            readOnly
                          />
                        </div>

                        <div className="ApplySoloStdFee-formGroup">
                          <label className="ApplySoloStdFee-formLabel">
                            Final Total
                          </label>
                          <input
                            type="text"
                            className="ApplySoloStdFee-formInput ApplySoloStdFee-finalTotal"
                            value={formatCurrency(finalTotal)}
                            readOnly
                          />
                        </div>

                        <div className="ApplySoloStdFee-formGroup">
                          <label className="ApplySoloStdFee-formLabel">
                            Amount Paid
                          </label>
                          <input
                            type="number"
                            className="ApplySoloStdFee-formInput"
                            value={totalPaid}
                            onChange={(e) =>
                              handleTotalPaidChange(e.target.value)
                            }
                            min="0"
                            step="0.01"
                            placeholder="Enter amount"
                          />
                        </div>

                        <div className="ApplySoloStdFee-formGroup">
                          <label className="ApplySoloStdFee-formLabel">
                            Balance Amount
                          </label>
                          <input
                            type="text"
                            className={`ApplySoloStdFee-formInput ${
                              lastPending > 0
                                ? "ApplySoloStdFee-pending"
                                : "ApplySoloStdFee-paid"
                            }`}
                            value={formatCurrency(lastPending)}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="ApplySoloStdFee-formActions">
                  <button
                    type="button"
                    className="ApplySoloStdFee-printBtn"
                    onClick={handlePrintReceipt}
                    disabled={!totalPaid || isSubmitting}
                  >
                    <FaFileInvoiceDollar className="ApplySoloStdFee-printIcon" />
                    Print Family Receipt
                  </button>

                  <button
                    type="submit"
                    className={`ApplySoloStdFee-submitBtn ${
                      isTotalRecordUpdateMode ? "ApplySoloStdFee-updateBtn" : ""
                    } ${isSubmitting ? "ApplySoloStdFee-submitting" : ""}`}
                    disabled={!totalPaid || lastPending < 0 || isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="ApplySoloStdFee-loading-small"></div>
                        Processing...
                      </>
                    ) : isTotalRecordUpdateMode ? (
                      <>
                        <FaEdit className="ApplySoloStdFee-submitIcon" />
                        Update Family Fee Record
                      </>
                    ) : (
                      <>
                        <FaPaperPlane className="ApplySoloStdFee-submitIcon" />
                        Submit New Family Record
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </section>
        )}

        {/* Quick Summary */}
        {familyCode && familyData && (
          <section className="ApplySoloStdFee-summarySection">
            <div className="ApplySoloStdFee-summaryCard">
              <div className="ApplySoloStdFee-summaryHeader">
                <FaChartLine className="ApplySoloStdFee-summaryIcon" />
                <h3>Family Fee Summary</h3>
                <div className="ApplySoloStdFee-summaryStatus">
                  {isTotalRecordUpdateMode ? "UPDATE MODE" : "NEW RECORD"}
                </div>
              </div>

              <div className="ApplySoloStdFee-summaryGrid">
                <div className="ApplySoloStdFee-summaryItem">
                  <div className="ApplySoloStdFee-summaryIconWrapper">
                    <FaUsers />
                  </div>
                  <div className="ApplySoloStdFee-summaryContent">
                    <div className="ApplySoloStdFee-summaryLabel">
                      Total Students
                    </div>
                    <div className="ApplySoloStdFee-summaryValue">
                      {totalStudents}
                    </div>
                  </div>
                </div>

                <div className="ApplySoloStdFee-summaryItem">
                  <div className="ApplySoloStdFee-summaryIconWrapper">
                    <FaMoneyBillWave />
                  </div>
                  <div className="ApplySoloStdFee-summaryContent">
                    <div className="ApplySoloStdFee-summaryLabel">
                      Total Payable
                    </div>
                    <div className="ApplySoloStdFee-summaryValue">
                      {formatCurrency(summary.totalPayable)}
                    </div>
                  </div>
                </div>

                <div className="ApplySoloStdFee-summaryItem">
                  <div className="ApplySoloStdFee-summaryIconWrapper">
                    <FaPercent />
                  </div>
                  <div className="ApplySoloStdFee-summaryContent">
                    <div className="ApplySoloStdFee-summaryLabel">Discount</div>
                    <div className="ApplySoloStdFee-summaryValue">
                      {summary.discount}% (
                      {formatCurrency(summary.discountAmount)})
                    </div>
                  </div>
                </div>

                <div className="ApplySoloStdFee-summaryItem">
                  <div className="ApplySoloStdFee-summaryIconWrapper">
                    <FaCalculator />
                  </div>
                  <div className="ApplySoloStdFee-summaryContent">
                    <div className="ApplySoloStdFee-summaryLabel">
                      Final Total
                    </div>
                    <div className="ApplySoloStdFee-summaryValue ApplySoloStdFee-summaryTotal">
                      {formatCurrency(finalTotal)}
                    </div>
                  </div>
                </div>

                <div className="ApplySoloStdFee-summaryItem">
                  <div className="ApplySoloStdFee-summaryIconWrapper">
                    <FaCreditCard />
                  </div>
                  <div className="ApplySoloStdFee-summaryContent">
                    <div className="ApplySoloStdFee-summaryLabel">
                      Amount Paid
                    </div>
                    <div className="ApplySoloStdFee-summaryValue ApplySoloStdFee-summaryPaid">
                      {formatCurrency(summary.paid)}
                    </div>
                  </div>
                </div>

                <div className="ApplySoloStdFee-summaryItem">
                  <div className="ApplySoloStdFee-summaryIconWrapper">
                    <FaHistory />
                  </div>
                  <div className="ApplySoloStdFee-summaryContent">
                    <div className="ApplySoloStdFee-summaryLabel">Balance</div>
                    <div
                      className={`ApplySoloStdFee-summaryValue ${
                        summary.balance > 0
                          ? "ApplySoloStdFee-summaryPending"
                          : "ApplySoloStdFee-summaryCleared"
                      }`}
                    >
                      {formatCurrency(summary.balance)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Footer */}
        {familyCode && familyData && (
          <footer className="ApplySoloStdFee-footer">
            <div className="ApplySoloStdFee-footerContent">
              <div className="ApplySoloStdFee-footerLeft">
                <div className="ApplySoloStdFee-footerLogo">
                  <FaUsers className="ApplySoloStdFee-footerIcon" />
                  Family Fee Management System
                </div>
                <p className="ApplySoloStdFee-footerText">
                  Processing fees for Family <strong>{familyCode}</strong> |
                  Students: {totalStudents} | Period:{" "}
                  {moment(startMonth).format("MMM YYYY")} -{" "}
                  {moment(endMonth).format("MMM YYYY")} | Mode:{" "}
                  {isTotalRecordUpdateMode ? "Update" : "New"}
                </p>
              </div>

              <div className="ApplySoloStdFee-footerRight">
                <div className="ApplySoloStdFee-systemStatus">
                  <div
                    className={`ApplySoloStdFee-statusDot ${
                      systemStatus === "ready"
                        ? "ApplySoloStdFee-statusReady"
                        : systemStatus === "updating" ||
                          systemStatus === "submitting"
                        ? "ApplySoloStdFee-statusProcessing"
                        : "ApplySoloStdFee-statusWarning"
                    }`}
                  ></div>
                  {systemStatus.replace("_", " ").toUpperCase()}
                </div>
                <div className="ApplySoloStdFee-lastUpdated">
                  Last updated: {moment().format("DD MMM YYYY, hh:mm A")}
                </div>
              </div>
            </div>
          </footer>
        )}

        {/* Empty State */}
        {!familyCode && (
          <section className="ApplySoloStdFee-emptyState">
            <div className="ApplySoloStdFee-emptyStateCard">
              <div className="ApplySoloStdFee-emptyStateIcon">
                <FaUsers />
              </div>
              <h3>Search for a Family</h3>
              <p>
                Enter a Family Code above to apply fees and expenses for family
                students.
              </p>
              <div className="ApplySoloStdFee-emptyStateTips">
                <FaInfoCircle className="ApplySoloStdFee-tipsIcon" />
                <div>
                  <p>
                    <strong>Family Code Format:</strong> F-DEAXXX (e.g.,
                    F-DEA101, F-DEA202)
                  </p>
                  <p>
                    <strong>Note:</strong> Only family students can be managed
                    from this screen.
                  </p>
                  <p>
                    <strong>Solo Students:</strong> Please use the Solo Student
                    Fee Management section.
                  </p>
                  <p>
                    <strong>API Endpoint:</strong> {apiEndpoint}
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ApplyFamStdFee;